import { client } from "../api/client.js";
import { renderPreview } from "../ui/previews.js";
import { formatDateMDY, formatDateTimeMDY12h, toDatetimeLocalValue } from "../utils/date.js";
import { renderPageHeader } from "../ui/pageHeader.js";
import * as blog from "./create-blog.js";
import * as social from "./create-social.js";
import * as video from "./create-video.js";
import { SOCIAL_TEMPLATES } from "../data/socialTemplates.js";
import { openGenerateMonthWizard } from '../ui/generateMonthWizard.js';
import { handleAiAssistClick } from '../ui/aiAssistToolbar.js';
import { updateCharCounter, setCharCounterPlatform } from '../ui/charCounter.js';
import { track } from '../utils/analytics.js';

const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

export class CreatePage {
  constructor() {
    this.loading = false;
    this.mode = "ai-complete"; // ai-complete | ai-assisted | scheduler-only
    this.items = []; // Normalized: [{ id, platform, text, title?, meta, scheduledAt? }]
    this.selectedItemId = null;
    this.activeDraft = null; // Phase F2.2: Single draft buffer
    this.error = null;

    // State-backed config
    this.topic = "";
    this.sessionContext = "";
    this.platforms = { facebook: true, instagram: true, x: true, linkedin: true, tiktok: false, youtube: false };
    this.campaignMode = false;
    this.campaignName = "";
    this.campaignId = null; // Persisted campaign ID
    this.campaignModalOpen = false;
    this.draftFilterTab = 'social';

    // Phase 4A Blog Editor State (Phase 37 added 'video' tab)
    this.activeCreateMode = localStorage.getItem("activeCreateMode") || "social"; // social | blog | video
    this.blogPosts = [];
    this.activeBlogPost = null;
    this.blogDirty = false;
    this.blogSavedAt = null;
    this.blogMode = "ai-complete"; // ai-complete | ai-assisted | manual
    this.blogTemplate = "none";
    this.blogTagRegistry = [];
    this.blogGenerating = false;
    this.blogError = null;
    this.blogNotice = null;
    this.blogBannerTimeout = null;
    // Phase 37 Commit 7 — faceless video derivative state
    this.blogIncludeVideo = false;
    this.blogVideoGenerating = false;
    this.blogVideoDerivatives = [];
    this.blogDraftsDrawerOpen = false; // Phase 2: Slide-over drawer state
    this.blogEditorView = this.blogEditorView || 'visual'; // 'visual'|'text'
    this.blogToolbarExpanded = !!this.blogToolbarExpanded;
    this.blogTextColor = this.blogTextColor || '#111827';
    // Phase 38 — Featured image AI generation state
    this.blogFeaturedAiGenerating = false;
    this.blogFeaturedAiError = null;
    this.socialPreviewCollapsed = !!this.socialPreviewCollapsed;

    // Tone & Templates
    this.brandVoice = ''; // Fetched from Brain on mount
    this.tone = localStorage.getItem('heist_tone') || 'brand'; // Default to brand voice
    this.customTone = localStorage.getItem('heist_custom_tone') || '';
    this.postLength = localStorage.getItem('heist_post_length') || 'medium';
    this.socialTemplate = 'freeform';
    this.templatePickerOpen = false;

    // Config drawer + preview
    this.configDrawerOpen = false;
    this.previewPlatform = null;

    // Attached media
    this.attachedMedia = []; // [{ name, size, type, dataUrl, dimensions? }]

    // Social Drafts State
    this.socialDraftsDrawerOpen = false;
    this.socialDrafts = [];
    this.socialDraftQuery = '';
    this.socialDraftsLoading = false;
    this.socialDraftsError = null;

    // Phase 37 — Video tab state (faceless slideshow pipeline)
    this.videoScript = '';
    this.videoVoices = [];
    this.videoVoicesLoading = false;
    this.videoVoicesError = null;
    this.videoVoiceId = null;
    this.videoVoiceIsBrandDefault = false;
    this.videoImageSource = 'stock'; // 'stock' | 'ai'
    this.videoGenerating = false;
    this.videoStageLabel = null;
    this.videoStagePercent = 0;
    this.videoStageStartedAt = null;
    this.videoStageInterval = null;
    this.videoResult = null;
    this.videoError = null;
    // Commit 5 polish
    this.videoInputMode = 'paste'; // 'paste' | 'topic'
    this.videoTopic = '';
    this.videoToneNotes = '';
    this.videoScriptGenerating = false;
    this.videoPlatform = 'tiktok';
    // Phase 37.5 — visual style: 'slideshow' (default, cheap) | 'ai_video' (Runway, premium)
    this.videoVisualStyle = 'slideshow';
    this.videoCostEstimate = null;
    this.videoCostExpanded = (() => { try { return localStorage.getItem('heist_video_cost_expanded') === '1'; } catch (_) { return false; } })();
    this.videoRegenContext = { voice: '', visuals: '', script: '' };
    // Commit 5b — library image picker
    this.videoLibraryImages = [];
    this.videoLibraryLoading = false;
    this.videoSelectedImageUrls = [];

    // Phase 1 Enterprise Social Workflow
    this.socialDirty = false;
    this.socialSavedAt = null;
    this.socialScheduledAt = null;
    this.socialToast = null;
    this.socialNextSteps = null; // { kind: 'saved'|'scheduled', ts }
    this.pendingNavHref = null;

    // Phase 4Persistence
    try {
      this.blogActionsCollapsed = sessionStorage.getItem("create.blogActionsCollapsed") === "true";
    } catch (e) {
      this.blogActionsCollapsed = false;
    }

    this.generatedBatchId = null; // Deprecated, use campaignId
    this.splitTimeout = null;

    // Load state from localStorage if available
    this.loadState();

    // Simple-first mode (Phase 2.1): on a brand-new account that has
    // never generated anything AND never explicitly dismissed simple-
    // first, hide the Social/Blog mode toggle and the AI-mode segmented
    // control. The user gets a stripped layout — single input, platform
    // chips, Generate button — and reveals the rest with the
    // "Show advanced options ▾" disclosure.
    try {
      const hasGenerated = !!localStorage.getItem('create_config_v1');
      const dismissed = localStorage.getItem('heist_create_simple_dismissed') === '1';
      this.simpleFirst = !hasGenerated && !dismissed;
    } catch (_) {
      this.simpleFirst = false;
    }
  }

  exitSimpleFirst() {
    if (!this.simpleFirst) return;
    this.simpleFirst = false;
    try { localStorage.setItem('heist_create_simple_dismissed', '1'); } catch (_) {}
    this.render();
  }

  loadState() {
    try {
      const saved = localStorage.getItem("create_config_v1");
      if (saved) {
        const parsed = JSON.parse(saved);
        this.mode = parsed.mode || "ai-complete";
        this.topic = parsed.topic || "";
        this.sessionContext = parsed.sessionContext || "";
        this.platforms = parsed.platforms || { facebook: true, instagram: true, x: true, linkedin: true, tiktok: false, youtube: false };
        this.campaignMode = parsed.campaignMode || false;
        this.campaignName = parsed.campaignName || "";
        this.campaignId = parsed.campaignId || null;
        // Don't restore items/selectedItemId to avoid stale content issues on reload,
        // unless we want full persistence. For now, config persistence is the requirement.
      }
    } catch (e) {
      console.warn("Failed to load create state", e);
    }
  }

  saveState() {
    try {
      const state = {
        mode: this.mode,
        topic: this.topic,
        sessionContext: this.sessionContext,
        platforms: this.platforms,
        campaignMode: this.campaignMode,
        campaignName: this.campaignName,
        campaignId: this.campaignId
      };
      localStorage.setItem("create_config_v1", JSON.stringify(state));
    } catch (e) {
      console.warn("Failed to save create state", e);
    }
  }

  async mount(container) {
    this.container = container;

    // Parse URL params (e.g. #create?scheduleDate=2026-03-25&mode=blog)
    const hashParts = window.location.hash.split('?');
    if (hashParts[1]) {
      const params = new URLSearchParams(hashParts[1]);
      const scheduleDate = params.get('scheduleDate');
      if (scheduleDate) {
        this.prefilledScheduleDate = scheduleDate + 'T10:00';
      }
      const modeParam = params.get('mode');
      if (modeParam === 'blog' || modeParam === 'social' || modeParam === 'video') {
        this.activeCreateMode = modeParam;
        localStorage.setItem('activeCreateMode', modeParam);
      }
      // Deep-link: open a specific blog post (e.g. from the Library)
      const openBlogId = params.get('blogId');
      if (openBlogId) {
        this._pendingBlogPostId = openBlogId;
        this.activeCreateMode = 'blog';
        localStorage.setItem('activeCreateMode', 'blog');
      }
    }

    // Fetch brand voice from Brain for tone dropdown
    try {
      const brain = await client.getBrainSummary();
      if (brain.profile?.voice) {
        this.brandVoice = brain.profile.voice;
      }
    } catch (_) { /* non-critical */ }

    if (this.activeCreateMode === 'blog') {
      await this.loadBlogPosts();
      this.refreshBlogSnippets();
      this._loadWpConnection();
      // Auto-open a specific blog post if deep-linked (?blogId=...)
      if (this._pendingBlogPostId) {
        const pendingId = this._pendingBlogPostId;
        this._pendingBlogPostId = null;
        this.loadBlogPostIntoEditor(pendingId).catch(() => {});
      }
    } else if (this.activeCreateMode === 'social') {
      await this.loadSocialDrafts();
    } else if (this.activeCreateMode === 'video') {
      // Phase 37: prefetch voices when landing on the Video tab
      video.loadVoices(this).catch(() => {});
    }

    this.render();

    // Crash recovery: check for unsaved localStorage backups
    this._checkCrashRecovery();

    this._beforeUnloadHandler = (e) => {
      if (this.activeCreateMode === 'social' && this.socialDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', this._beforeUnloadHandler);

  }

  _checkCrashRecovery() {
    try {
      const socialBackup = localStorage.getItem('heist_autosave_social');
      if (socialBackup) {
        const data = JSON.parse(socialBackup);
        if (data.content && Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          const indicator = this.container.querySelector('#autosave-indicator');
          if (indicator) {
            indicator.innerHTML = `Recovered draft <button class="btn-link" style="font-size:11px;margin-left:4px" id="restore-social-backup">Restore</button> <button class="btn-link" style="font-size:11px;margin-left:2px;color:var(--text-muted)" id="dismiss-social-backup">Dismiss</button>`;
            indicator.className = 'autosave-indicator visible';
            this.container.querySelector('#restore-social-backup')?.addEventListener('click', () => {
              const item = this.selectedItemId ? this.items.find(i => i.id === this.selectedItemId) : this.activeDraft;
              if (item) { item.text = data.content; item.content = data.content; this.socialDirty = true; this.render(); }
              localStorage.removeItem('heist_autosave_social');
            });
            this.container.querySelector('#dismiss-social-backup')?.addEventListener('click', () => {
              localStorage.removeItem('heist_autosave_social');
              indicator.className = 'autosave-indicator';
              indicator.innerHTML = '';
            });
          }
        } else {
          localStorage.removeItem('heist_autosave_social');
        }
      }

      const blogBackup = localStorage.getItem('heist_autosave_blog');
      if (blogBackup) {
        const data = JSON.parse(blogBackup);
        if (data.content_html && Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          const feedback = this.container.querySelector('#save-feedback') || this.container.querySelector('.create-feedback');
          if (feedback) {
            feedback.innerHTML = `Recovered blog draft <button class="btn-link" style="font-size:11px;margin-left:4px" id="restore-blog-backup">Restore</button> <button class="btn-link" style="font-size:11px;margin-left:2px;color:var(--text-muted)" id="dismiss-blog-backup">Dismiss</button>`;
            feedback.className = 'create-feedback success';
            this.container.querySelector('#restore-blog-backup')?.addEventListener('click', () => {
              if (this.activeBlogPost) {
                this.activeBlogPost.title = data.title || this.activeBlogPost.title;
                this.activeBlogPost.content_html = data.content_html;
                this.blogDirty = true;
                this.render();
              }
              localStorage.removeItem('heist_autosave_blog');
            });
            this.container.querySelector('#dismiss-blog-backup')?.addEventListener('click', () => {
              localStorage.removeItem('heist_autosave_blog');
              feedback.className = 'create-feedback';
              feedback.innerHTML = '';
            });
          }
        } else {
          localStorage.removeItem('heist_autosave_blog');
        }
      }
    } catch (e) {
      console.warn('[CRASH_RECOVERY]', e);
    }
  }

  getEffectiveTone() {
    if (this.tone === 'brand') return this.brandVoice || 'Professional';
    if (this.tone === 'Other') return this.customTone?.trim() || 'Professional';
    return this.tone;
  }

  render() {
    try {
      this.ensureSocialDraftSelected();

      // Phase F2.2: Determine what to show in editor
      let selectedItem = null;
      if (this.selectedItemId) {
        selectedItem = this.items.find(i => i.id === this.selectedItemId);
      } else if (this.activeDraft) {
        selectedItem = this.activeDraft;
      } else if (this.items.length > 0 && !this.selectedItemId) {
        // Fallback: auto-select first generated item if no draft and items exist
        this.selectedItemId = this.items[0].id;
        selectedItem = this.items[0];
      }

      this.container.innerHTML = `
        <div class="create-page ${this.simpleFirst ? 'is-simple-first' : ''} ${this.socialPreviewCollapsed ? 'social-preview-collapsed' : ''} ${this.blogActionsCollapsed ? 'blog-actions-collapsed' : ''} ${this.mode === 'ai-complete' ? 'mode-complete' : this.mode === 'ai-assisted' ? 'mode-assist' : this.mode === 'scheduler-only' ? 'mode-manual' : ''}" data-testid="create-page-wrapper">
          ${this.renderUnsavedGuard()}
        ${renderPageHeader({
          eyebrow: "AI-POWERED CREATION",
          title: "Content Studio",
          subtitle: "Create and refine your next move.",
          tourPlaylist: 'create',
          headerLeftExtraHtml: this._renderFreeGenerationsBadge(),
          rightHtml: this.simpleFirst ? `
                <button class="btn-studio-sec" id="gmw-create-launch" style="font-size:12px;padding:6px 14px;white-space:nowrap;"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-1px;margin-right:3px;"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>Plan a Month</button>
          ` : `
                ${this.activeCreateMode === 'social' ? `
                <div class="segmented-control create-mode-toggle" role="tablist" aria-label="Social create mode" data-testid="social-mode-segments">
                  <button class="segmented-control__btn create-mode__btn ${this.mode === 'ai-complete' ? 'is-active active' : ''}" data-mode="ai-complete" data-testid="mode-ai-complete">AI Complete</button>
                  <button class="segmented-control__btn create-mode__btn ${this.mode === 'ai-assisted' ? 'is-active active' : ''}" data-mode="ai-assisted" data-testid="mode-ai-assisted">AI Assisted</button>
                  <button class="segmented-control__btn create-mode__btn ${this.mode === 'scheduler-only' ? 'is-active active' : ''}" data-mode="scheduler-only" data-testid="mode-scheduler-only">Manual</button>
                </div>
                <button class="btn-studio-sec" id="gmw-create-launch" style="margin-left:8px;font-size:12px;padding:6px 14px;white-space:nowrap;"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-1px;margin-right:3px;"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>Plan a Month</button>
                ` : ''}
                ${this.activeCreateMode === 'blog' ? `
                <div class="segmented-control create-mode-toggle blog-mode-toggle" role="tablist" aria-label="Blog create mode" data-testid="blog-mode-segments">
                  <button class="segmented-control__btn create-mode__btn ${this.blogMode === 'ai-complete' ? 'is-active active' : ''}" data-blog-mode="ai-complete">AI Complete</button>
                  <button class="segmented-control__btn create-mode__btn ${this.blogMode === 'ai-assisted' ? 'is-active active' : ''}" data-blog-mode="ai-assisted">AI Assisted</button>
                  <button class="segmented-control__btn create-mode__btn ${this.blogMode === 'manual' ? 'is-active active' : ''}" data-blog-mode="manual">Manual</button>
                </div>
                ` : ''}
          `
        })}

        <div class="create-workspace page-shell page-content">
            <div class="blog-banner-slot" data-testid="blog-banner" style="grid-column: 1 / -1; margin-bottom: 16px;"></div>
            ${this.simpleFirst ? `
              <button class="create-simple-first-trigger" data-testid="create-show-advanced" id="create-show-advanced">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
                <span>Show advanced options</span>
                <span class="create-simple-first-trigger__hint">— blog editor, modes, manual scheduling</span>
              </button>
            ` : this.renderModeToggle()}
            ${this.activeCreateMode === 'social' && this.mode === 'scheduler-only' ? '<div class="manual-notice"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> Manual mode — write and schedule content without AI.</div>' : ''}
            ${this.activeCreateMode === 'social' && this.mode === 'scheduler-only' ? this.renderSchedulerOnlyBar() : ''}
            ${this.activeCreateMode === 'social'
                ? this.renderSocialWorkbench(selectedItem)
                : this.activeCreateMode === 'video'
                    ? this.renderVideoWorkbench()
                    : this.renderBlogWorkbench()}
          </div>

          ${this.activeCreateMode === 'blog' ? this.renderBlogDraftsDrawer() : ''}
          ${this.activeCreateMode === 'social' ? this.renderSocialDraftsDrawer() : ''}
          ${this.activeCreateMode === 'social' ? this.renderConfigDrawer() : ''}
          ${this.renderCampaignModal()}
        </div>
      `;

      this.renderBlogBannerUI();

      // Populate the sidebar snippets list after every blog render. Safe to
      // call from social mode too — no-op when the list element is missing.
      if (this.activeCreateMode === 'blog') {
        this.renderBlogSidebarSnippets();
      }

      this.bindEvents();
    } catch (err) {
      console.error('[CREATE_RENDER_ERROR]', err);
      this.container.innerHTML = `<div class="create-error-state">Error: ${_esc(err.message)}</div>`;
    }
  }

  renderUnsavedGuard() {
    if (!this.pendingNavHref) return '';
    return `
      <div class="toast-banner warn" style="position:fixed; top:20px; left:50%; transform:translateX(-50%); width:auto; max-width:90vw; z-index:9999; box-shadow:0 10px 30px rgba(0,0,0,0.2);">
        <div style="display:flex; align-items:center; gap:24px;">
          <span><strong>Unsaved Changes:</strong> Do you want to save your progress before leaving?</span>
          <div style="display:flex; gap:12px;">
            <button class="btn-primary" id="guard-save" style="background:var(--surface-1); color:var(--warning); border:none; padding:6px 14px; font-size:13px; font-weight:700; border-radius:6px; cursor:pointer;">Save Draft & Leave</button>
            <button class="btn-secondary" id="guard-discard" style="background:rgba(255,255,255,0.2); border:1px solid rgba(255,255,255,0.4); color:white; padding:6px 14px; font-size:13px; font-weight:600; border-radius:6px; cursor:pointer;">Discard</button>
            <button class="btn-secondary" id="guard-stay" style="background:transparent; border:none; color:white; padding:6px 14px; font-size:13px; font-weight:500; cursor:pointer; text-decoration:underline;">Stay</button>
          </div>
        </div>
      </div>
    `;
  }

  renderModeToggle() {
    const socialIcon = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>';
    const blogIcon = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';

    return `
      <div class="studio-mode-tabs" data-testid="create-mode-toggle">
        <button class="smt ${this.activeCreateMode === 'social' ? 'active' : ''}" data-workbench="social" data-mode="social" data-testid="create-toggle-social" role="tab" tabindex="0">
          <div class="smt-icon">${socialIcon}</div>
          <div class="smt-info">
            <div class="smt-title">Social Posts</div>
            <div class="smt-desc">Generate for Facebook, Instagram, X, LinkedIn, TikTok, YouTube, Pinterest, Reddit simultaneously</div>
          </div>
        </button>
        <button class="smt ${this.activeCreateMode === 'blog' ? 'active' : ''}" data-workbench="blog" data-mode="blog" data-testid="create-toggle-blog" role="tab" tabindex="0">
          <div class="smt-icon">${blogIcon}</div>
          <div class="smt-info">
            <div class="smt-title">Blog Post</div>
            <div class="smt-desc">Long-form editor with AI assist and SEO guidance</div>
          </div>
        </button>
        <button class="smt ${this.activeCreateMode === 'video' ? 'active' : ''}" data-workbench="video" data-mode="video" data-testid="create-toggle-video" role="tab" tabindex="0">
          <div class="smt-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg></div>
          <div class="smt-info">
            <div class="smt-title">Faceless Video</div>
            <div class="smt-desc">Script → voiceover → slideshow MP4 in under 90 seconds</div>
          </div>
        </button>
      </div>
    `;
  }

  renderGenerateBar() { return social.renderGenerateBar(this); }

  renderPreviewPanel(selectedItem) { return social.renderPreviewPanel(this, selectedItem); }

  renderConfigDrawer() { return social.renderConfigDrawer(this); }

  renderSocialWorkbench(selectedItem) { return social.renderSocialWorkbench(this, selectedItem); }

  // Phase 37 — Video tab delegation
  renderVideoWorkbench() { return video.renderVideoWorkbench(this); }

  renderSchedulerOnlyBar() {
    const isChecked = (p) => this.platforms[p] ? 'checked' : '';
    return `
      <div class="scheduler-bar" data-testid="scheduler-bar">
        <details class="paste-import-details">
          <summary class="paste-import-summary">Paste &amp; Import Content</summary>
          <div class="paste-import-body">
            <p class="create-helper-text" style="margin:0 0 8px;font-size:12px;color:var(--text-muted);">Paste one or more posts below. Choose a platform, then click Import.</p>
            <textarea id="paste-input" class="create-textarea" rows="4" placeholder="Paste your content here..." data-testid="paste-input"></textarea>
            <div class="create-helper-actions" style="display:flex; gap:8px; align-items:center; margin-top:8px; flex-wrap:wrap;">
              <span style="font-size:12px;color:var(--text-muted);">Platform:</span>
              <select id="paste-platform-select" class="create-select" data-testid="paste-platform">
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="x">X (Twitter)</option>
                <option value="linkedin">LinkedIn</option>
              </select>
              <button class="btn-secondary sm" id="parse-btn" data-testid="parse-btn">Import</button>
              <span class="create-helper-text" style="font-size:11px;color:var(--text-muted);">Separate multiple posts with a blank line</span>
            </div>
          </div>
        </details>
      </div>
    `;
  }

  renderBlogWorkbench() { return blog.renderBlogWorkbench(this); }

  renderBlogDraftsList() {
    return `
      <div class="blog-card blog-drafts-card">
        <h2 class="blog-section-title">Recent Drafts</h2>
        
        <div class="blog-drafts-search-wrapper">
          <input type="text" class="blog-drafts-search" placeholder="Search drafts..." value="${this.blogDraftQuery || ''}" data-testid="blog-drafts-search" />
        </div>

        <div class="blog-drafts-list" data-testid="blog-drafts-list">
          ${this.renderBlogDraftItems()}
        </div>
      </div>
    `;
  }

  renderBlogEditor() { return blog.renderBlogEditor(this); }

  renderBlogDraftsDrawer() { return blog.renderBlogDraftsDrawer(this); }

  renderBlogActions() { return blog.renderBlogActions(this); }

  createNewDraft(platform = 'facebook') {
    return {
      id: `new-draft-${Date.now()}`, // Temporary ID
      dbId: null,
      platform: platform,
      title: '',
      text: '',
      scheduledAt: null,
      status: 'draft',
      type: 'social',
      isNew: true, // Flag to identify new draft
      meta: {}
    };
  }

  handleNewDraft() {
    this.activeDraft = this.createNewDraft();
    this.selectedItemId = null; // Deselect list items
    this.render();
  }

  async loadSocialDrafts() { return social.loadSocialDrafts(this); }

  ensureSocialDraftSelected() {
    if (this.activeCreateMode !== 'social') return;

    const hasSelected = !!this.selectedItemId;
    const hasDraft = !!this.activeDraft;
    const hasItems = Array.isArray(this.items) && this.items.length > 0;

    if (!hasSelected && !hasDraft && !hasItems) {
      // Seed a blank draft so editor appears immediately on fresh load
      const firstChecked = Object.keys(this.platforms || {}).find(p => this.platforms[p]) || 'facebook';
      this.activeDraft = this.createNewDraft(firstChecked);
      this.selectedItemId = null;
    }
  }

  renderCampaignModal() {
    return `
      <div class="campaign-overlay ${this.campaignModalOpen ? 'open' : ''}" data-testid="campaign-overlay">
        <div class="campaign-modal" data-testid="campaign-modal">
          <div class="campaign-modal__shimmer"></div>
          <div class="campaign-modal-head">
            <span class="campaign-modal-title">New Campaign</span>
            <button class="campaign-modal-close" data-testid="campaign-modal-close">&times;</button>
          </div>
          <div class="campaign-modal-body">
            <div class="campaign-field">
              <label class="campaign-lbl">Campaign Name</label>
              <input type="text" class="campaign-inp" id="campaign-modal-name" placeholder="e.g. Spring Product Launch" value="">
            </div>
            <div class="campaign-field">
              <label class="campaign-lbl">Goal</label>
              <textarea class="campaign-inp" id="campaign-modal-goal" rows="2" placeholder="What do you want to achieve?"></textarea>
            </div>
            <div class="campaign-field">
              <label class="campaign-lbl">Platforms</label>
              <div class="cmd-chips">
                <div class="chip on" data-modal-platform="facebook"><span class="chip-dot" style="background:#1877F2"></span>FB</div>
                <div class="chip on" data-modal-platform="instagram"><span class="chip-dot" style="background:#E4405F"></span>IG</div>
                <div class="chip on" data-modal-platform="x"><span class="chip-dot" style="background:#94A3B8"></span>X</div>
                <div class="chip on" data-modal-platform="linkedin"><span class="chip-dot" style="background:#0A66C2"></span>LI</div>
              </div>
            </div>
            <div class="campaign-field">
              <label class="campaign-lbl">Total Posts</label>
              <select class="campaign-sel" id="campaign-modal-posts">
                <option value="3">3 posts</option>
                <option value="5" selected>5 posts</option>
                <option value="7">7 posts</option>
                <option value="10">10 posts</option>
                <option value="14">14 posts</option>
              </select>
            </div>
            <div class="campaign-field">
              <label class="campaign-lbl">Timeframe</label>
              <select class="campaign-sel" id="campaign-modal-timeframe">
                <option value="1 week">1 week</option>
                <option value="2 weeks" selected>2 weeks</option>
                <option value="1 month">1 month</option>
              </select>
            </div>
            <div class="campaign-field">
              <label class="campaign-lbl">Include current draft?</label>
              <div style="display:flex;gap:12px;margin-top:4px">
                <label style="display:flex;align-items:center;gap:5px;font-size:13px;cursor:pointer;color:var(--text)"><input type="radio" name="campaign-include-draft" value="yes"> Yes</label>
                <label style="display:flex;align-items:center;gap:5px;font-size:13px;cursor:pointer;color:var(--text)"><input type="radio" name="campaign-include-draft" value="no" checked> No</label>
              </div>
            </div>
          </div>
          <div class="campaign-modal-foot">
            <button class="btn-secondary sm" data-testid="campaign-modal-cancel">Cancel</button>
            <button class="btn-primary sm" data-testid="save-campaign-stay">Save &amp; keep editing</button>
            <button class="campaign-btn-go" data-testid="save-campaign-go">Save &amp; go to campaign</button>
          </div>
        </div>
      </div>
    `;
  }

  async _saveCampaignFromModal(navigate) {
    const name = this.container.querySelector('#campaign-modal-name')?.value?.trim();
    if (!name) {
      this.socialToast = { msg: 'Campaign name is required', kind: 'warn' };
      this.render();
      return;
    }
    const goal = this.container.querySelector('#campaign-modal-goal')?.value?.trim() || '';
    const chips = this.container.querySelectorAll('[data-modal-platform].on');
    const platforms = Array.from(chips).map(c => c.dataset.modalPlatform);
    const totalPosts = parseInt(this.container.querySelector('#campaign-modal-posts')?.value || '5', 10);
    const timeframe = this.container.querySelector('#campaign-modal-timeframe')?.value || '2 weeks';
    const includeDraft = this.container.querySelector('input[name="campaign-include-draft"]:checked')?.value === 'yes';

    try {
      const res = await client.createCampaign({ name, goal, platforms, totalPosts, timeframe });
      if (includeDraft && res?.campaign?.id) {
        const item = this.activeDraft || this.items.find(i => i.id === this.selectedItemId);
        if (item) {
          this.campaignId = res.campaign.id;
          this.campaignName = name;
          this.campaignMode = true;
        }
      }
      this.campaignModalOpen = false;
      if (navigate) {
        window.location.hash = '#campaigns';
      } else {
        this.socialToast = { msg: `Campaign "${name}" created`, kind: 'success' };
        this.render();
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => { this.socialToast = null; this.render(); }, 3000);
      }
    } catch (err) {
      this.socialToast = { msg: 'Failed to create campaign', kind: 'error' };
      this.render();
    }
  }

  renderSocialDraftsDrawer() { return social.renderSocialDraftsDrawer(this); }

  renderSocialDraftItems() { return social.renderSocialDraftItems(this); }

  renderPlatformSelector() { return social.renderPlatformSelector(this); }

  renderConfigCard() { return social.renderConfigCard(this); }

  renderItemsListCard() { return social.renderItemsListCard(this); }

  renderEditorCard(selectedItem) { return social.renderEditorCard(this, selectedItem); }

  renderPreviewCard(selectedItem) { return social.renderPreviewCard(this, selectedItem); }

  // --- Logic --- //

  addNewDraft() {
    const newId = this.generateId();
    this.items.push({
      id: newId,
      platform: 'facebook', // default
      text: '',
      title: '',
      status: 'draft',
      meta: {}
    });
    this.selectedItemId = newId;
  }

  handleAutoSplit(text) {
    if (!text || !text.trim()) return;

    if (this.splitTimeout) clearTimeout(this.splitTimeout);

    this.splitTimeout = setTimeout(() => {
      const rawItems = text.split(/\n\s*\n/).filter(t => t.trim());
      if (rawItems.length === 0) return;

      const platform = this.container.querySelector('input[name="platform"]:checked')?.value || 'facebook';

      this.items = rawItems.map(chunk => ({
        id: this.generateId(),
        platform: platform,
        text: chunk.trim(),
        title: '',
        status: 'draft',
        meta: {}
      }));

      // Auto-select first item
      if (this.items.length > 0) {
        this.selectedItemId = this.items[0].id;
        this.activeDraft = null; // Clear active draft if we imported batch
      }

      this.render();

      // Restore focus
      setTimeout(() => {
        const input = this.container.querySelector('#paste-input');
        if (input) { input.value = text; input.focus(); }
      }, 0);
    }, 500);
  }

  async handleGenerate() {
    this.loading = true;
    this.render(); // Show spinner

    try {
      // Validation
      if (!this.topic && this.mode !== 'scheduler-only') throw new Error("Topic required");

      // Get active platforms from state
      const activePlatforms = Object.keys(this.platforms).filter(p => this.platforms[p]);
      console.log('[DEBUG_GENERATE] Active platforms:', activePlatforms);
      if (activePlatforms.length === 0) throw new Error("Select at least one platform");

      // Immediate Campaign Creation
      if (this.campaignMode) {
        if (!this.campaignName) throw new Error("Campaign Name required");

        // Create campaign now if not exists
        if (!this.campaignId) {
          const campRes = await client.createCampaign({
            name: this.campaignName,
            goal: this.topic || 'Batch Generation',
            platforms: activePlatforms,
            cadence: { totalItems: activePlatforms.length }
          });
          this.campaignId = campRes.id || campRes.data?.campaignId;
          this.saveState(); // Persist ID
        }
      }

      const templatePrompt = SOCIAL_TEMPLATES.find(t => t.key === this.socialTemplate)?.prompt || '';
      const genAiImageCheck = document.getElementById('gen-ai-image-check');
      const genAiVideoCheck = document.getElementById('gen-ai-video-check');
      const genFacelessVideoCheck = document.getElementById('gen-faceless-video-check');
      const wantAiImages = genAiImageCheck?.checked || false;
      const wantAiVideos = genAiVideoCheck?.checked || false;
      const wantFacelessVideo = genFacelessVideoCheck?.checked || false;
      const res = await client.generateBatch({ topic: this.topic, platforms: activePlatforms, toneOverride: this.getEffectiveTone(), postLength: this.postLength, generateAiImages: wantAiImages, generateAiVideos: wantAiVideos, generateFacelessVideo: wantFacelessVideo, sessionContext: templatePrompt ? `[CONTENT FRAMEWORK] ${templatePrompt}` : undefined });
      console.log('[DEBUG_GENERATE] Backend response:', res);
      const drafts = Array.isArray(res.data) ? res.data : (res.data?.drafts || res.drafts || []);
      console.log('[DEBUG_GENERATE] Parsed drafts:', drafts);
      if (drafts.length > 0) {
        track('draft_batch_generated', { count: drafts.length, stubbed: !!res.stub });
      }

      if (res.warning) {
        this.socialToast = { msg: res.warning, kind: 'warn' };
      }
      if (wantAiImages && !res.stub) {
        this.socialToast = { msg: 'AI images are being generated for your posts. They will appear when you view posts in the Library.', kind: 'info' };
      }
      if (wantFacelessVideo && !res.stub) {
        const videoEligible = ['tiktok', 'instagram_reels', 'reels', 'youtube_shorts', 'shorts', 'youtube'];
        const eligibleCount = activePlatforms.filter(p => videoEligible.includes(p)).length;
        if (eligibleCount > 0) {
          this.socialToast = {
            msg: `Generating ${eligibleCount} faceless video${eligibleCount === 1 ? '' : 's'} in the background. They'll appear in Library as each render finishes (~60–90s each).`,
            kind: 'info',
          };
        } else {
          this.socialToast = {
            msg: 'Faceless video is checked but no video-eligible platform is selected (TikTok / Reels / Shorts / YouTube). Skipped.',
            kind: 'warn',
          };
        }
      }

      // Update existing items or create new ones — preserve items created by chip selection
      activePlatforms.forEach((p, idx) => {
        const text = drafts[idx]?.text || drafts[0]?.text || "Generated content...";
        const existing = this.items.find(i => i.platform === p);
        if (existing) {
          // Update existing item with AI-generated content
          existing.text = text;
          existing.title = this.topic.substring(0, 50);
          existing.meta = { ...(existing.meta || {}), hashtags: drafts[idx]?.hashtags || [] };
        } else {
          this.items.push({
            id: this.generateId(),
            platform: p,
            text: text,
            title: this.topic.substring(0, 50),
            status: 'draft',
            meta: { hashtags: drafts[idx]?.hashtags || [] }
          });
        }
      });
      // Remove items for platforms that are no longer selected
      this.items = this.items.filter(i => activePlatforms.includes(i.platform));

      if (this.items.length > 0) {
        this.selectedItemId = this.items[0].id;
        this.activeDraft = null; // Clear active draft on method gen
      }

    } catch (err) {
      console.error('[DEBUG_GENERATE] Exception caught:', err);
      this.error = err.message;
    } finally {
      this.loading = false;
      this.render(); // Re-render with items (and config fields preserved via state)
      if (this.socialToast) {
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => { this.socialToast = null; this.render(); }, 5000);
      }
    }
  }

  async handleGenerateSingle() {
    this.loading = true;
    this.render();
    try {
      if (!this.topic) throw new Error("Add a topic first.");
      const activePlatforms = Object.keys(this.platforms).filter(p => this.platforms[p]);
      const platform = activePlatforms[0] || 'facebook';

      const singleTemplatePrompt = SOCIAL_TEMPLATES.find(t => t.key === this.socialTemplate)?.prompt || '';
      const res = await client.generateBatch({ topic: this.topic, platforms: [platform], toneOverride: this.getEffectiveTone(), postLength: this.postLength, sessionContext: singleTemplatePrompt ? `[CONTENT FRAMEWORK] ${singleTemplatePrompt}` : undefined });
      const drafts = Array.isArray(res.data) ? res.data : (res.data?.drafts || res.drafts || []);
      const text = drafts[0]?.text || "Generated single post...";

      const newItem = {
        id: this.generateId(),
        platform: platform,
        text: text,
        title: `Single: ${this.topic.substring(0, 30)} `,
        status: 'draft',
        meta: { hashtags: drafts[0]?.hashtags || [] }
      };

      this.items.unshift(newItem);
      this.selectedItemId = newItem.id;
      this.activeDraft = null;
    } catch (err) {
      this.error = err.message;
    } finally {
      this.loading = false;
      this.render();
    }
  }

  async handleGenerateCampaignQuick() {
    this.loading = true;
    this.campaignMode = true;
    if (!this.campaignName) this.campaignName = `Quick Campaign: ${this.topic || 'New'} `;
    this.render();

    try {
      if (!this.topic) throw new Error("Add a topic for the campaign.");
      const activePlatforms = Object.keys(this.platforms).filter(p => this.platforms[p]);
      if (activePlatforms.length === 0) throw new Error("Select at least one platform.");

      // Create campaign first
      const campRes = await client.createCampaign({
        name: this.campaignName,
        goal: this.topic,
        platforms: activePlatforms,
        cadence: { totalItems: 7 }
      });
      this.campaignId = campRes.id || campRes.data?.campaignId;
      this.saveState();

      const campTemplatePrompt = SOCIAL_TEMPLATES.find(t => t.key === this.socialTemplate)?.prompt || '';
      const res = await client.generateBatch({ topic: this.topic, platforms: activePlatforms, count: 7, toneOverride: this.getEffectiveTone(), postLength: this.postLength, sessionContext: campTemplatePrompt ? `[CONTENT FRAMEWORK] ${campTemplatePrompt}` : undefined });
      const drafts = res.data.drafts || [];

      this.items = drafts.map((d, i) => ({
        id: this.generateId(),
        platform: activePlatforms[i % activePlatforms.length],
        text: d.text,
        title: `Day ${i + 1}: ${this.topic.substring(0, 20)} `,
        status: 'draft',
        meta: { hashtags: d.hashtags || [] }
      }));

      if (this.items.length > 0) {
        this.selectedItemId = this.items[0].id;
        this.activeDraft = null;
      }
    } catch (err) {
      this.error = err.message;
    } finally {
      this.loading = false;
      this.render();
    }
  }

  /**
   * Core save logic extracted for both manual save and autosave.
   * @param {Object} options - { silent: boolean } silent=true for autosave (no UI clear)
   */
  async _saveToServer(options = {}) {
    const { silent = false } = options;

    let selectedItem = null;
    if (this.selectedItemId) {
      selectedItem = this.items.find(i => i.id === this.selectedItemId);
    } else if (this.activeDraft) {
      selectedItem = this.activeDraft;
    }

    if (!selectedItem) return;

    const status = selectedItem.scheduledAt ? 'scheduled' : 'draft';
    selectedItem.status = status;

    if (this.campaignMode && this.campaignName && !this.campaignId) {
      const campRes = await client.createCampaign({
        name: this.campaignName,
        goal: 'Batch from Content Studio',
        platforms: ['facebook'],
        cadence: { totalItems: 1 }
      });
      this.campaignId = campRes.id || campRes.data?.campaignId;
      this.saveState();
    }

    // Resolve media for this item — prefer the item's own attached list,
    // fall back to the current editor's attachedMedia (covers the case where
    // the draft hasn't been promoted yet). Null if empty so the server stores null.
    const resolvedMedia = (Array.isArray(selectedItem.media) && selectedItem.media.length > 0)
      ? selectedItem.media
      : (Array.isArray(this.attachedMedia) && this.attachedMedia.length > 0 ? this.attachedMedia : null);

    const isUpdate = !!selectedItem.dbId;
    if (isUpdate) {
      await client.updateContentItem(selectedItem.dbId, {
        content: selectedItem.text,
        title: selectedItem.title,
        scheduled_at: selectedItem.scheduledAt,
        status: status,
        context: selectedItem.context || '',
        media: resolvedMedia,
        // Persist per-post destination options (Pinterest board / Reddit
        // subreddit, etc.) so the publish connector can read them. The server
        // syncs metadata.platform_options into scheduled_posts.platform_options.
        metadata: selectedItem.meta || {}
      });
    } else {
      const payload = {
        items: [{
          platform: selectedItem.platform,
          content: selectedItem.text,
          title: selectedItem.title,
          scheduledAt: selectedItem.scheduledAt || null,
          status: status,
          type: 'social',
          rootId: this.campaignId,
          metadata: selectedItem.meta || {},
          context: selectedItem.context || '',
          media: resolvedMedia
        }]
      };
      const res = await client.saveToLibrary(payload);
      if (res.ids && res.ids.length > 0) {
        selectedItem.dbId = res.ids[0];
        delete selectedItem.isNew;
        if (this.activeDraft && selectedItem === this.activeDraft) {
          this.items.unshift(selectedItem);
          this.selectedItemId = selectedItem.id;
          this.activeDraft = null;
        }
      }
    }

    this.socialDirty = false;
    return selectedItem;
  }

  async handleSave() {
    const btn = this.container.querySelector('#save-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }

    try {
      const savedItem = await this._saveToServer();
      if (!savedItem) return;

      // Clear the editor after manual save
      this.selectedItemId = null;
      this.activeDraft = null;

      this.render();
      const label = savedItem.status === 'scheduled' ? 'Scheduled ✓' : 'Saved to Library ✓';
      this.showFeedback(label);
    } catch (err) {
      console.error(err);
      this.showFeedback('Error saving', true);
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = 'Save Changes'; }
    }
  }

  async autoSave() {
    // Only autosave items that have already been saved once
    const selectedItem = this.selectedItemId
      ? this.items.find(i => i.id === this.selectedItemId)
      : this.activeDraft;
    if (!selectedItem || !selectedItem.dbId) return;
    if (!this.socialDirty) return;

    // localStorage backup for crash recovery
    try {
      const content = selectedItem.text || selectedItem.content || '';
      if (content) {
        localStorage.setItem('heist_autosave_social', JSON.stringify({
          content, platform: selectedItem.platform, timestamp: Date.now()
        }));
      }
    } catch(e) { /* localStorage may be full */ }

    const indicator = this.container.querySelector('#autosave-indicator');
    try {
      if (indicator) { indicator.textContent = 'Saving...'; indicator.className = 'autosave-indicator autosave-indicator--saving'; }
      await this._saveToServer({ silent: true });
      if (indicator) { indicator.textContent = 'Saved ✓'; indicator.className = 'autosave-indicator autosave-indicator--saved'; }
      // Clear localStorage backup after successful server save
      try { localStorage.removeItem('heist_autosave_social'); } catch(e) {}
      setTimeout(() => {
        if (indicator) indicator.className = 'autosave-indicator';
      }, 3000);
    } catch (err) {
      console.error('[AUTOSAVE]', err);
      if (indicator) { indicator.textContent = 'Error'; indicator.className = 'autosave-indicator autosave-indicator--error'; }
    }
  }

  scheduleAutosave() {
    if (this._autosaveTimer) clearTimeout(this._autosaveTimer);
    this._autosaveTimer = setTimeout(() => this.autoSave(), 2000);
  }

  showFeedback(msg, isError = false) {
    const el = this.container.querySelector('#save-feedback');
    if (el) {
      el.textContent = msg;
      el.className = `create-feedback ${isError ? 'error' : 'success'}`;
      setTimeout(() => el.textContent = '', 1500);
    }
  }

  // F-008 — small badge above the prompt bar that shows remaining free
  // generations. Only renders for users with no BYOK key AND credits left.
  // When credits hit 0, swap to an "Add your AI key" CTA so the user knows
  // exactly what unblocks them.
  _renderFreeGenerationsBadge() {
    const u = window.__auth__ || {};
    if (typeof u.free_generations_remaining !== 'number') return '';
    // Suppress when the user has connected ANY AI provider key — they're paying
    // their own way, no need for the trial messaging.
    if (u.has_ai_key) return '';
    // Phase 36: when the platform-credit limit is 0, no trial path exists —
    // suppress the badge entirely. The Generate button itself will surface
    // CONFIG_MISSING_KEY pointing the user to Settings → AI Settings.
    const limit = u.free_generations_limit || 0;
    if (limit <= 0) return '';
    const remaining = u.free_generations_remaining;
    if (remaining <= 0) {
      return `
        <div class="free-gen-badge free-gen-badge--exhausted" style="margin-top:10px;display:inline-flex;align-items:center;gap:8px;padding:6px 12px;border-radius:999px;background:rgba(239,68,68,0.10);border:1px solid rgba(239,68,68,0.30);font-size:12px;color:var(--danger);">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span>Free generations used. Keep generating free from Claude or ChatGPT — <a href="#connectors" style="color:inherit;text-decoration:underline;font-weight:600;">connect Heist to your AI chat</a>, or <a href="#settings?section=section-ai-settings" style="color:inherit;text-decoration:underline;">add your own key</a>.</span>
        </div>`;
    }
    // Only nag when running low (last 2). Otherwise show a discreet badge.
    const lowWater = remaining <= 2;
    return `
      <div class="free-gen-badge${lowWater ? ' free-gen-badge--low' : ''}" style="margin-top:10px;display:inline-flex;align-items:center;gap:8px;padding:6px 12px;border-radius:999px;background:${lowWater ? 'rgba(245,158,11,0.10)' : 'rgba(139,92,246,0.10)'};border:1px solid ${lowWater ? 'rgba(245,158,11,0.30)' : 'rgba(139,92,246,0.25)'};font-size:12px;color:${lowWater ? 'var(--warning, #f59e0b)' : 'var(--text-muted)'};">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/></svg>
        <span><strong>${remaining}</strong> of ${limit} free generation${remaining === 1 ? '' : 's'} left${lowWater ? ' \u2014 ' : '. '}<a href="#settings?section=section-ai-settings" style="color:inherit;text-decoration:underline;">Add your AI key</a> ${lowWater ? 'to keep going' : 'to unlock unlimited'}.</span>
      </div>`;
  }

  getPlatformIcon(p) { return { facebook: 'FB', instagram: 'IG', x: 'X', linkedin: 'LI', tiktok: 'TT', youtube: 'YT', pinterest: 'PIN', reddit: 'RD' }[p] || 'P'; }
  truncate(t, l) { return t && t.length > l ? t.substr(0, l) + '...' : t || 'New Draft'; }
  generateId() { return `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`; }
  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  handleMediaFiles(files) {
    const maxSize = 500 * 1024 * 1024; // 500MB

    Array.from(files).forEach(file => {
      // Accept any image/* or video/* MIME type (covers HEIC, WebP, etc.)
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) return;
      if (file.size > maxSize) return;

      // Videos: use Object URL directly (skip readAsDataURL to avoid memory bloat)
      if (file.type.startsWith('video/')) {
        const media = { name: file.name, size: file.size, type: file.type, dataUrl: URL.createObjectURL(file), dimensions: null };
        const video = document.createElement('video');
        video.preload = 'metadata';
        let handled = false;
        const pushMedia = () => {
          if (handled) return;
          handled = true;
          this.attachedMedia.push(media);
          this.updatePreviewWithMedia();
          this.render();
        };
        video.onloadedmetadata = () => {
          media.dimensions = `${Math.floor(video.duration / 60)}:${(Math.floor(video.duration) % 60).toString().padStart(2, '0')}`;
          media.duration = Math.floor(video.duration);
          media.videoWidth = video.videoWidth;
          media.videoHeight = video.videoHeight;
          pushMedia();
        };
        video.onerror = () => pushMedia();
        setTimeout(() => pushMedia(), 3000);
        video.src = media.dataUrl;
      } else {
        // Images + other files: use FileReader
        const reader = new FileReader();
        reader.onload = (ev) => {
          const media = { name: file.name, size: file.size, type: file.type, dataUrl: ev.target.result, dimensions: null };
          if (file.type.startsWith('image/')) {
            const img = new Image();
            img.onload = () => {
              media.dimensions = `${img.width}x${img.height}`;
              this.attachedMedia.push(media);
              this.updatePreviewWithMedia();
              this.render();
            };
            img.src = ev.target.result;
          } else {
            this.attachedMedia.push(media);
            this.updatePreviewWithMedia();
            this.render();
          }
        };
        reader.readAsDataURL(file);
      }

      // Auto-upload to workspace media library (fire-and-forget)
      if (window.__auth__?.workspace_id) {
        const fd = new FormData();
        fd.append('file', file);
        client.uploadWorkspaceMedia(fd)
          .then(res => console.log('[MEDIA_UPLOAD] Uploaded to workspace library:', file.name, res))
          .catch(err => console.warn('[MEDIA_UPLOAD] Failed:', file.name, err.message || err));
      }
    });
  }

  removeMedia(index) {
    this.attachedMedia.splice(index, 1);
    this.updatePreviewWithMedia();
    this.render();
  }

  updatePreviewWithMedia() {
    // Update the preview panel with the attached media
    const previewContainer = this.container?.querySelector('.preview-scroll [data-testid="preview-container"]');
    if (!previewContainer) return;

    let item = null;
    if (this.selectedItemId) item = this.items.find(i => i.id === this.selectedItemId);
    else if (this.activeDraft) item = this.activeDraft;
    if (!item) return;

    // Store media reference on the item for preview rendering
    item.media = this.attachedMedia.length > 0 ? this.attachedMedia : null;

    const brandName = window.__auth__?.name || "Your Brand";
    const activePlatform = this.previewPlatform || item.platform || 'facebook';
    const previewHtml = renderPreview(activePlatform, item.text, item.title, brandName, item.media);
    previewContainer.innerHTML = previewHtml;
  }

  // --- Live Update --- //
  updateLivePreview(key, value) {
    let item = null;
    if (this.selectedItemId) item = this.items.find(i => i.id === this.selectedItemId);
    else if (this.activeDraft) item = this.activeDraft;

    if (item) {
      item[key] = value;
      if (this.activeCreateMode === 'social') {
        this.socialDirty = true;
        this.scheduleAutosave();
        // Update status pill visibility without full render
        const statusContainer = this.container.querySelector('.social-editor-status');
        if (statusContainer && !statusContainer.querySelector('.status-pill.warn')) {
          statusContainer.innerHTML = '<span class="status-pill warn">Unsaved</span>';
        }
      }
      // Optimization: just update specific DOM elements instead of full render
      if (key === 'text' || key === 'platform') { // Also update on platform change
        const brandName = window.__auth__?.name || "Your Brand";
        const activePlatform = this.previewPlatform || item.platform || 'facebook';
        const media = item.media || (this.attachedMedia && this.attachedMedia.length > 0 ? this.attachedMedia : null);

        // If a video is playing in the preview, only update text nodes to avoid resetting playback
        const newPreview = this.container.querySelector('.preview-scroll [data-testid="preview-container"]');
        const playingVideo = newPreview?.querySelector('video');
        const videoIsActive = playingVideo && (playingVideo.currentTime > 0 || !playingVideo.paused);

        if (key === 'text' && videoIsActive) {
          // Just update text in the preview body without rebuilding the whole preview
          const pvBodies = newPreview.querySelectorAll('.pv-body');
          pvBodies.forEach(el => { el.textContent = value; });
        } else {
          const previewHtml = renderPreview(activePlatform, item.text, item.title, brandName, media);
          const oldPreview = this.container.querySelector('.create-preview-container');
          if (oldPreview) oldPreview.innerHTML = renderPreview(item.platform, item.text, item.title, brandName, media);
          if (newPreview) newPreview.innerHTML = previewHtml;
        }

        if (key === 'text') {
          updateCharCounter('create-char-counter', value.length);
          // Only update list item if it exists in list
          if (this.selectedItemId) {
            const listP = this.container.querySelector(`.create-item-card[data-item-id="${item.id}"] .create-item-preview`);
            if (listP) listP.textContent = this.truncate(value, 60);
          }
        }
        if (key === 'platform') {
          setCharCounterPlatform('create-char-counter', value, (item.text || '').length);
        }
        if (key === 'context') {
          // No preview update needed for context itself
        }
      }
    }
  }

  setBlogError(msg) {
    this.blogError = msg;
    this.blogNotice = null;
    if (this.blogBannerTimeout) clearTimeout(this.blogBannerTimeout);
    this.renderBlogBannerUI();
  }

  setBlogNotice(msg) {
    if (typeof msg === 'string') {
      this.blogNotice = { message: msg, type: 'notice' };
    } else {
      this.blogNotice = msg;
    }
    this.blogError = null;
    if (this.blogBannerTimeout) clearTimeout(this.blogBannerTimeout);
    this.renderBlogBannerUI();

    // Auto-dismiss notices only, not confirms
    if (this.blogNotice.type === 'notice') {
      this.blogBannerTimeout = setTimeout(() => {
        this.blogNotice = null;
        this.renderBlogBannerUI();
      }, 4000);
    }
  }

  clearBlogBanner() {
    this.blogError = null;
    this.blogNotice = null;
    if (this.blogBannerTimeout) clearTimeout(this.blogBannerTimeout);
    this.renderBlogBannerUI();
  }

  renderBlogBannerUI() {
    const slot = this.container.querySelector('.blog-banner-slot');
    if (!slot) return;

    if (this.blogError) {
      slot.innerHTML = `<div class="blog-banner blog-banner--error">${this.blogError} <button class="banner-close">&times;</button></div>`;
    } else if (this.blogNotice) {
      const { message, type, onConfirm, onCancel, platform, url, title } = this.blogNotice;
      if (type === 'confirm') {
        slot.innerHTML = `
      <div class="blog-banner blog-banner--confirm">
            <span class="banner-message">${message}</span>
            <div class="banner-actions">
              <button class="banner-btn banner-btn--primary btn-confirm">Clear & Apply</button>
              <button class="banner-btn banner-btn--cancel btn-cancel">Cancel</button>
            </div>
          </div>
      `;
        slot.querySelector('.btn-confirm').onclick = () => onConfirm();
        slot.querySelector('.btn-cancel').onclick = () => onCancel();
      } else if (type === 'published') {
        const destLabel = platform === 'wordpress' ? 'WordPress' : platform === 'website' ? 'your website' : 'your library';
        const safeTitle = title ? ` — <span class="blog-published-title">${this.escapeForBanner(title)}</span>` : '';
        const viewLink = url ? `<a class="banner-btn banner-btn--ghost" href="${url}" target="_blank" rel="noopener">View live →</a>` : '';
        slot.innerHTML = `
          <div class="blog-banner blog-banner--published" role="status">
            <div class="banner-message">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <span>Published to ${destLabel}${safeTitle}.</span>
            </div>
            <div class="banner-actions">
              ${viewLink}
              <button class="banner-btn banner-btn--primary btn-new-post">Start a new post</button>
              <button class="banner-close" aria-label="Dismiss">&times;</button>
            </div>
          </div>
        `;
        const newBtn = slot.querySelector('.btn-new-post');
        if (newBtn) newBtn.onclick = () => this.startNewBlogPost();
      } else {
        slot.innerHTML = `<div class="blog-banner blog-banner--notice">${message} <button class="banner-close">&times;</button></div>`;
      }
    } else {
      slot.innerHTML = '';
    }

    const closeBtn = slot.querySelector('.banner-close');
    if (closeBtn) {
      closeBtn.onclick = () => this.clearBlogBanner();
    }
  }

  escapeForBanner(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  startNewBlogPost() {
    this.activeBlogPost = {
      id: `new-draft-${Date.now()}`,
      title: '',
      content_html: '',
      status: 'draft',
      template_key: this.blogTemplate,
      mode: this.blogMode
    };
    this.blogDirty = false;
    this.blogSavedAt = null;
    this.topic = '';
    this.sessionContext = '';
    this.blogSectionPrompt = '';
    this.blogPublishPlatform = null;
    this.clearBlogBanner();
    this.render();
  }

  renderBlogDirtyUI() {
    const indicator = this.container.querySelector('.blog-dirty-indicator');
    if (this.blogDirty) {
      if (!indicator) {
        const meta = this.container.querySelector('.blog-editor-meta');
        if (meta) meta.insertAdjacentHTML('afterbegin', '<span class="blog-dirty-indicator">● Unsaved changes</span>');
      }
    } else {
      if (indicator) indicator.remove();
    }
  }

  renderBlogMetricsUI() {
    const items = this.container.querySelectorAll('.checklist-items li');
    if (items.length) {
      items[0].className = this.activeBlogPost?.title ? 'done' : '';
      const text = (this.activeBlogPost?.content_html || '').replace(/<[^>]*>?/gm, ' ');
      const wc = text.split(/\s+/).filter(w => w.length > 0).length;
      const wordsDone = wc >= 300;
      items[1].className = wordsDone ? 'done' : '';
      items[1].textContent = `~300 words (${wc})`;
      // CTA check
      const html = (this.activeBlogPost?.content_html || '').toLowerCase();
      const hasCta = html.includes('cta') || html.includes('call to action') || html.includes('sign up') || html.includes('get started') || html.includes('learn more') || html.includes('subscribe') || html.includes('buy now') || html.includes('contact us');
      if (items[2]) items[2].className = hasCta ? 'done' : '';
      // Saved check
      if (items[3]) items[3].className = (!this.blogDirty && this.activeBlogPost?.id && !String(this.activeBlogPost.id).startsWith('new-')) ? 'done' : '';
    }
  }

  renderDirtyUI() {
    this.renderBlogDirtyUI();
    this.renderBlogMetricsUI();
  }

  handleTemplateChange(templateKey) { return blog.handleTemplateChange(this, templateKey); }

  bindEvents() {
    this.container.onclick = async (e) => {
      // Workbench Toggle
      const toggleBtn = e.target.closest('[data-testid="create-mode-toggle"] .segmented-control__btn');
      if (toggleBtn) {
        this.activeCreateMode = toggleBtn.dataset.mode;
        localStorage.setItem("activeCreateMode", this.activeCreateMode);
        if (this.activeCreateMode === 'blog') {
          if (!this.activeBlogPost) {
            this.activeBlogPost = { title: '', content_html: '' };
          }
          await this.loadBlogPosts();
        }
        this.render();
        return;
      }

      // Blog-actions-toggle: works in BOTH blog and social modes
      if (e.target.closest('[data-testid="blog-actions-toggle"]')) {
        this.blogActionsCollapsed = !this.blogActionsCollapsed;
        try { sessionStorage.setItem("create.blogActionsCollapsed", String(this.blogActionsCollapsed)); } catch (_) { }
        this.render();
        return;
      }

      if (this.activeCreateMode === 'blog') {
        this.handleBlogClick(e);
      } else {
        const modeBtn = e.target.closest('.create-mode-toggle .create-mode__btn');
        if (modeBtn) {
          this.mode = modeBtn.dataset.mode;
          this.saveState();
          this.activeDraft = null;
          // Cancel any pending auto-split from paste input
          if (this.splitTimeout) { clearTimeout(this.splitTimeout); this.splitTimeout = null; }
          this.render();
          return;
        }
        await this.handleSocialClick(e);
      }
    };

    if (this.activeCreateMode === 'social') {
      this.bindSocialInputs();
    } else if (this.activeCreateMode === 'video') {
      video.bindVideoInputs(this);
    } else {
      this.bindBlogInputs();
    }

    // Studio mode toggle (large card toggle)
    this.container.querySelectorAll('.smt[data-workbench]').forEach(btn => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.workbench;
        if (mode !== this.activeCreateMode) {
          this.activeCreateMode = mode;
          localStorage.setItem("activeCreateMode", mode);
          if (mode === 'blog') {
            this.loadBlogPosts().then(() => { this.render(); this.refreshBlogSnippets(); this._loadWpConnection(); });
          } else if (mode === 'video') {
            // Phase 37 — render the Video tab immediately; voices load async.
            this.render();
            video.loadVoices(this).catch(() => {});
          } else {
            this.loadSocialDrafts().then(() => this.render());
          }
        }
      });
    });

    // Generate bar
    const genBarBtn = this.container.querySelector('#gen-bar-btn');
    if (genBarBtn) {
      genBarBtn.addEventListener('click', () => {
        const topicInput = this.container.querySelector('#gen-bar-topic');
        if (topicInput) this.topic = topicInput.value;
        const generateBtn = this.container.querySelector('#generate-btn');
        if (generateBtn) generateBtn.click();
        else this.handleGenerate();
      });
    }

    const genBarTopic = this.container.querySelector('#gen-bar-topic');
    if (genBarTopic) {
      // Respect manual resize: once user drags the corner, stop auto-sizing.
      let userResized = false;
      const MIN_H = 88;
      const MAX_H = 600;
      const autoResize = (el) => {
        if (userResized) return;
        el.style.height = 'auto';
        el.style.height = Math.max(MIN_H, Math.min(el.scrollHeight, MAX_H)) + 'px';
      };
      // Detect manual resize via pointerdown on the corner handle, not ResizeObserver
      // (observer causes feedback loops when flex layout recomputes widths).
      genBarTopic.addEventListener('pointerdown', (e) => {
        // Only the bottom-right ~18px square is the resize grabber.
        const rect = genBarTopic.getBoundingClientRect();
        if (e.clientX >= rect.right - 18 && e.clientY >= rect.bottom - 18) {
          userResized = true;
          genBarTopic.dataset.userResized = '1';
          // Lock current width into the inline style so flex switches to fixed.
          genBarTopic.style.width = rect.width + 'px';
        }
      });
      genBarTopic.addEventListener('input', (e) => {
        this.topic = e.target.value;
        autoResize(e.target);
      });
      autoResize(genBarTopic);
    }

    // Generate My Month wizard entry point
    const gmwCreateBtn = this.container.querySelector('#gmw-create-launch');
    if (gmwCreateBtn) {
      gmwCreateBtn.addEventListener('click', () => openGenerateMonthWizard());
    }

    // Simple-first → "Show advanced options" trigger. Flips off the
    // simpleFirst flag, persists the dismissal so the user never sees
    // it again, and re-renders to surface the full Studio chrome.
    const simpleAdvancedBtn = this.container.querySelector('#create-show-advanced');
    if (simpleAdvancedBtn) {
      simpleAdvancedBtn.addEventListener('click', () => this.exitSimpleFirst());
    }

    // Tone selector
    const toneSelect = this.container.querySelector('#gen-tone-select');
    if (toneSelect) {
      toneSelect.addEventListener('change', (e) => {
        this.tone = e.target.value;
        localStorage.setItem('heist_tone', this.tone);
        const customInput = this.container.querySelector('#gen-tone-custom');
        if (customInput) {
          customInput.style.display = this.tone === 'Other' ? 'block' : 'none';
        }
      });
    }

    // Custom tone input
    const toneCustom = this.container.querySelector('#gen-tone-custom');
    if (toneCustom) {
      toneCustom.addEventListener('input', (e) => {
        this.customTone = e.target.value;
        localStorage.setItem('heist_custom_tone', this.customTone);
      });
    }

    // Post length selector
    const lengthSelect = this.container.querySelector('#gen-length-select');
    if (lengthSelect) {
      lengthSelect.addEventListener('change', (e) => {
        this.postLength = e.target.value;
        localStorage.setItem('heist_post_length', this.postLength);
      });
    }

    // Template picker
    const templateBtn = this.container.querySelector('#gen-template-btn');
    if (templateBtn) {
      templateBtn.addEventListener('click', () => {
        this.templatePickerOpen = !this.templatePickerOpen;
        this.render();
      });
    }
    const templateClose = this.container.querySelector('#template-picker-close');
    if (templateClose) {
      templateClose.addEventListener('click', () => { this.templatePickerOpen = false; this.render(); });
    }
    this.container.querySelectorAll('[data-template-key]').forEach(card => {
      card.addEventListener('click', () => {
        this.socialTemplate = card.dataset.templateKey;
        this.templatePickerOpen = false;
        this.render();
      });
    });

    // Platform chips in generate bar — toggle platform AND create/remove Generated Posts items
    this.container.querySelectorAll('[data-chip-platform]').forEach(chip => {
      chip.addEventListener('click', () => {
        const p = chip.dataset.chipPlatform;
        this.platforms[p] = !this.platforms[p];
        chip.classList.toggle('on', this.platforms[p]);
        chip.classList.toggle('off', !this.platforms[p]);
        const cb = chip.querySelector('input[type="checkbox"]');
        if (cb) cb.checked = this.platforms[p];

        if (this.platforms[p]) {
          // Platform selected — add a draft item to Generated Posts if not already there
          if (!this.items.find(i => i.platform === p)) {
            const editorEl = this.container?.querySelector('[data-testid="content-editor"]');
            const sourceItem = this.activeDraft || this.items.find(i => i.id === this.selectedItemId);
            const text = sourceItem?.text || editorEl?.value?.trim() || '';
            this.items.push({
              id: this.generateId(),
              platform: p,
              text: text,
              title: '',
              status: 'draft',
              meta: {}
            });
            // Auto-select the new item if it's the first one
            if (this.items.length === 1) {
              this.selectedItemId = this.items[0].id;
              this.previewPlatform = p;
            }
            this.socialDirty = true;
          }
        } else {
          // Platform deselected — remove its item from Generated Posts
          const existingItem = this.items.find(i => i.platform === p);
          if (existingItem) {
            this.items = this.items.filter(i => i.platform !== p);
            // If we removed the selected item, select the first remaining
            if (this.selectedItemId === existingItem.id) {
              this.selectedItemId = this.items.length > 0 ? this.items[0].id : null;
              this.previewPlatform = this.items.length > 0 ? this.items[0].platform : 'facebook';
            }
            this.socialDirty = true;
          }
        }

        this.saveState();
        this.render();
      });
      // Sync chip state when checkbox is toggled programmatically (e.g. by tests)
      const cb = chip.querySelector('input[type="checkbox"]');
      if (cb) {
        cb.addEventListener('change', () => {
          const p = chip.dataset.chipPlatform;
          this.platforms[p] = cb.checked;
          chip.classList.toggle('on', cb.checked);
          chip.classList.toggle('off', !cb.checked);
          this.saveState();
        });
      }
    });

    // Config drawer
    const configDrawerOpenBtn = this.container.querySelector('#config-drawer-open-btn');
    if (configDrawerOpenBtn) {
      configDrawerOpenBtn.addEventListener('click', () => {
        this.configDrawerOpen = true;
        this.render();
      });
    }

    const configDrawerClose = this.container.querySelector('[data-testid="config-drawer-close"]');
    if (configDrawerClose) {
      configDrawerClose.addEventListener('click', () => {
        this.configDrawerOpen = false;
        this.render();
      });
    }

    const configDrawerOverlay = this.container.querySelector('[data-testid="config-drawer-overlay"]');
    if (configDrawerOverlay) {
      configDrawerOverlay.addEventListener('click', () => {
        this.configDrawerOpen = false;
        this.render();
      });
    }

    // Platform toggle pills in config drawer
    this.container.querySelectorAll('[data-platform-toggle]').forEach(pill => {
      pill.addEventListener('click', () => {
        const p = pill.dataset.platformToggle;
        this.platforms[p] = !this.platforms[p];
        this.saveState();
        this.render();
      });
    });

    // Preview platform tabs
    this.container.querySelectorAll('[data-preview-platform]').forEach(tab => {
      tab.addEventListener('click', () => {
        const platform = tab.dataset.previewPlatform;
        // Update the selected item's platform for preview
        if (this.selectedItemId) {
          const item = this.items.find(i => i.id === this.selectedItemId);
          if (item) {
            // Don't change the item's actual platform, just switch the preview tab
            this.previewPlatform = platform;
            this.render();
          }
        } else if (this.activeDraft) {
          this.previewPlatform = platform;
          this.render();
        }
      });
    });
  }

  async handleSocialClick(e) {
    if (!e || !e.target) return;

    if (e.target.id === 'guard-stay') {
      this.pendingNavHref = null;
      this.render();
      return;
    }
    if (e.target.id === 'guard-discard') {
      this.socialDirty = false;
      const href = this.pendingNavHref;
      this.pendingNavHref = null;
      window.location.hash = href;
      return;
    }
    if (e.target.id === 'guard-save') {
      await this.handleSave();
      this.socialDirty = false;
      const href = this.pendingNavHref;
      this.pendingNavHref = null;
      window.location.hash = href;
      return;
    }
    const modeBtn = e.target.closest('.create-mode__btn');
    if (modeBtn) {
      this.mode = modeBtn.dataset.mode;
      this.saveState();
      this.activeDraft = null;
      this.render();
      return;
    }

    if (e.target.closest('[data-testid="social-preview-toggle"]')) {
      this.socialPreviewCollapsed = !this.socialPreviewCollapsed;
      this.render();
      return;
    }

    if (e.target.id === 'btn-next-schedule') {
      this.container.querySelector('#editor-schedule')?.focus();
      return;
    }
    if (e.target.id === 'btn-next-calendar') {
      window.location.hash = '#calendar';
      return;
    }
    if (e.target.closest('[data-testid="next-view-library"]')) {
      window.location.hash = '#library';
      return;
    }

    // "Generate Blog" button — use AI to generate a full blog post from the social content
    if (e.target.closest('.social-generate-blog')) {
      // Gather topic from generate bar, editor content, or title
      const topicInput = this.container.querySelector('#gen-bar-topic');
      if (topicInput && topicInput.value) this.topic = topicInput.value;
      if (!this.topic) {
        const editorContent = this.container.querySelector('#editor-content');
        if (editorContent && editorContent.value.trim()) {
          this.topic = editorContent.value.trim();
        }
      }
      if (!this.topic) {
        const titleInput = this.container.querySelector('#editor-title');
        if (titleInput && titleInput.value.trim()) {
          this.topic = titleInput.value.trim();
        }
      }
      if (this.topic) {
        await this.handleGenerateBlogFromSocial(this.topic);
      } else {
        const genInput = this.container.querySelector('#gen-bar-topic');
        if (genInput) { genInput.focus(); genInput.placeholder = 'Enter a topic first to generate a blog...'; }
      }
      return;
    }

    if (e.target.closest('[data-testid="social-new-draft"]')) {
      if (this.socialDirty) {
        const decision = window.confirm("You have unsaved changes. Save draft before creating a new one?\n\nOK for 'Save & New', Cancel to 'Discard & New'.");
        if (decision) {
          await this.handleSave();
          this.socialSavedAt = new Date().toISOString();
        }
      }
      this.handleNewDraft();
      this.socialDirty = false;
      this.socialSavedAt = null;
      this.socialScheduledAt = null;
      this.socialNextSteps = null;
      this.render();
      return;
    }

    if (e.target.closest('[data-testid="social-duplicate"]')) {
      let item = this.activeDraft || this.items.find(i => i.id === this.selectedItemId);
      if (item) {
        this.activeDraft = {
          ...item,
          id: `new-draft-${Date.now()}`,
          dbId: null,
          title: `${item.title || 'Untitled'} (Copy)`,
          scheduledAt: null,
          status: 'draft',
          isNew: true
        };
        this.socialDirty = true;
        this.selectedItemId = null;
        this.socialToast = { msg: 'Duplicated into new draft', kind: 'success' };
        this.render();
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => { this.socialToast = null; this.render(); }, 2500);
      }
      return;
    }

    // Drafts tab switching
    const draftTab = e.target.closest('[data-draft-tab]');
    if (draftTab) {
      this.draftFilterTab = draftTab.dataset.draftTab;
      this.render();
      return;
    }

    if (e.target.closest('[data-testid="social-drafts-toggle"]')) {
      this.socialDraftsDrawerOpen = true;
      await this.loadSocialDrafts();
      this.render();
      return;
    }

    if (e.target.closest('[data-testid="social-drafts-overlay"]') || e.target.closest('[data-testid="social-drafts-close"]')) {
      this.socialDraftsDrawerOpen = false;
      this.render();
      return;
    }

    if (e.target.closest('[data-testid="social-drafts-new"]')) {
      const firstChecked = Object.keys(this.platforms || {}).find(p => this.platforms[p]) || 'facebook';
      this.activeDraft = this.createNewDraft(firstChecked);
      this.selectedItemId = null;
      this.socialDraftsDrawerOpen = false;
      this.render();
      return;
    }

    const draftItem = e.target.closest('.social-draft-item');
    if (draftItem) {
      const dbId = draftItem.dataset.dbId;
      const draft = this.socialDrafts.find(d => String(d.dbId || d.id) === String(dbId));
      if (draft) {
        // Restore attached media so the image survives a save → close → reopen cycle.
        const restoredMedia = Array.isArray(draft.media) && draft.media.length > 0 ? draft.media : null;
        this.activeDraft = {
          id: `lib-${dbId}`,
          dbId: draft.dbId || draft.id,
          platform: draft.platform,
          title: draft.title || '',
          text: draft.text || draft.content || '',
          scheduledAt: draft.scheduledAt,
          status: draft.status,
          isNew: false,
          meta: draft.meta || {},
          media: restoredMedia
        };
        // Hydrate the editor's attachedMedia state so the preview pane and
        // attached-media list render the image immediately on open.
        this.attachedMedia = restoredMedia ? [...restoredMedia] : [];
        this.selectedItemId = null;
        this.socialDraftsDrawerOpen = false;
        this.render();
      }
      return;
    }

    const regenBtn = e.target.closest('.regenerate-item-btn');
    if (regenBtn) {
      const id = regenBtn.dataset.itemId;
      await this.handleRegenerate(id);
      return;
    }

    const itemCard = e.target.closest('.create-item-card') || e.target.closest('.item-strip');
    if (itemCard) {
      const id = itemCard.dataset.itemId;
      if (id && id !== 'undefined') {
        this.selectedItemId = id;
        this.activeDraft = null;
        // Switch preview to the selected item's platform
        const item = this.items.find(i => i.id === id);
        if (item) this.previewPlatform = item.platform;
        this.render();
      }
      return;
    }

    if (e.target.closest('#empty-gen-single')) { this.handleGenerateSingle(); return; }
    if (e.target.closest('#empty-gen-campaign')) { this.handleGenerateCampaignQuick(); return; }
    if (e.target.closest('#empty-import')) {
      this.mode = 'scheduler-only';
      this.render();
      setTimeout(() => {
        const paste = this.container.querySelector('#paste-input');
        if (paste) {
          paste.scrollIntoView({ behavior: 'smooth' });
          paste.focus();
        }
      }, 100);
      return;
    }
    if (e.target.closest('#empty-blank')) { this.handleNewDraft(); return; }

    if (e.target.closest('#btn-new-draft')) { this.handleNewDraft(); return; }
    if (e.target.closest('#btn-scroll-config')) {
      this.container.querySelector('.create-config-section').scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (e.target.closest('#add-draft-btn')) { this.addNewDraft(); this.render(); return; }

    // Parse button: explicit parse for scheduler-only mode
    if (e.target.closest('#parse-btn') || e.target.closest('[data-testid="parse-btn"]')) {
      // Cancel any pending auto-split so it doesn't overwrite items later
      if (this.splitTimeout) { clearTimeout(this.splitTimeout); this.splitTimeout = null; }
      const pasteInput = this.container.querySelector('#paste-input');
      const platformSelect = this.container.querySelector('#paste-platform-select');
      const text = pasteInput?.value;
      const platform = platformSelect?.value || 'facebook';
      if (!text || !text.trim()) return;
      const rawItems = text.split(/\n\s*\n/).filter(t => t.trim());
      if (rawItems.length === 0) return;
      this.items = rawItems.map(chunk => ({
        id: this.generateId(),
        platform: platform,
        text: chunk.trim(),
        title: '',
        status: 'draft',
        meta: {}
      }));
      if (this.items.length > 0) {
        this.selectedItemId = this.items[0].id;
        this.activeDraft = null;
      }
      this.render();
      return;
    }

    // AI Assist — immediate action on any button (shared handler)
    if (e.target.closest('[data-assist-action]') || e.target.closest('[data-action="fill-ai"]') || e.target.closest('[data-action="regenerate"]')) {
      let item = this.activeDraft || this.items.find(i => i.id === this.selectedItemId);
      const editorEl = this.container.querySelector('[data-testid="content-editor"]');
      if (item && !item.text && editorEl?.value?.trim()) {
        item.text = editorEl.value.trim();
      }
      const handled = await handleAiAssistClick(
        e,
        () => editorEl?.value || item?.text || '',
        (text) => {
          if (item) { item.text = text; this.socialDirty = true; }
          if (editorEl) editorEl.value = text;
        },
        item?.platform || 'general',
        (msg, kind) => { this.socialToast = { msg, kind }; this.render(); }
      );
      if (handled) return;
    }

    // Fill to All Posts — copy current item content to all other items
    if (e.target.closest('[data-action="fill-to-all"]')) {
      const currentItem = this.activeDraft || this.items.find(i => i.id === this.selectedItemId);
      const editorEl = this.container?.querySelector('[data-testid="content-editor"]');
      const text = editorEl?.value?.trim() || currentItem?.text || '';
      if (!text) {
        this.socialToast = { msg: 'Write some content first before filling to all posts.', kind: 'info' };
        this.render();
        return;
      }
      // Sync current editor content to the model first
      if (currentItem && editorEl) currentItem.text = editorEl.value;
      let filled = 0;
      for (const item of this.items) {
        if (item.id !== this.selectedItemId) {
          item.text = text;
          filled++;
        }
      }
      this.socialDirty = true;
      this.socialToast = { msg: `Content copied to ${filled} other post${filled !== 1 ? 's' : ''}.`, kind: 'success' };
      this.render();
      return;
    }

    // JSON Export
    if (e.target.closest('[data-testid="export-json-btn"]') || e.target.closest('#btn-export-json')) {
      if (this.items.length === 0) return;
      const exportData = this.items.map(item => ({
        platform: item.platform,
        title: item.title,
        content: item.text,
        scheduledAt: item.scheduledAt,
        status: item.status
      }));
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `content-batch-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    }

    if (e.target.closest('#generate-btn')) { this.handleGenerate(); return; }
    // blog-actions-toggle handled in bindEvents (both modes)

    if (e.target.closest('#save-btn') || e.target.closest('#save-draft-btn')) {
      await this.handleSave();
      if (this.activeCreateMode === 'social') {
        this.socialDirty = false;
        this.socialSavedAt = new Date().toISOString();
        this.socialToast = {
          msg: 'Draft saved!', kind: 'success',
          actions: [{ label: 'View in library', action: 'nav-library' }]
        };
        this.socialNextSteps = { kind: 'saved', ts: this.socialSavedAt };
        await this.loadSocialDrafts();
        this.render();
        this.showFeedback('Saved ✓');
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => { this.socialToast = null; this.render(); }, 5000);
      }
      return;
    }

    if (e.target.closest('#schedule-post-btn')) {
      let item = this.activeDraft || this.items.find(i => i.id === this.selectedItemId);
      if (!item || !item.scheduledAt) {
        this.socialToast = { msg: 'Pick a date/time first', kind: 'warn' };
        this.render();
        this.container.querySelector('#editor-schedule')?.focus();
        return;
      }
      await this.handleSave();
      this.socialDirty = false;
      this.socialScheduledAt = item.scheduledAt;
      // Clear editor after scheduling
      this.activeDraft = null;
      this.selectedItemId = null;
      this.items = [];
      this.topic = '';
      this.sessionContext = '';
      this.attachedMedia = [];
      this.ensureSocialDraftSelected();
      this.socialToast = {
        msg: 'Post scheduled!', kind: 'success',
        actions: [
          { label: 'New post', action: 'new-post' },
          { label: 'View in calendar', action: 'nav-calendar' }
        ]
      };
      this.socialNextSteps = { kind: 'scheduled', ts: this.socialScheduledAt };
      await this.loadSocialDrafts();
      this.render();
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => { this.socialToast = null; this.render(); }, 5000);
      return;
    }

    // Schedule All button
    if (e.target.closest('#btn-schedule-all')) {
      const btn = this.container.querySelector('#btn-schedule-all');
      if (btn) { btn.disabled = true; btn.textContent = 'Scheduling...'; }
      let scheduled = 0;
      try {
        // Fetch user's schedule rules for optimal posting times
        let rules = [];
        try {
          const rulesRes = await client.getScheduleRules();
          rules = rulesRes.rules || rulesRes.data || [];
        } catch(e) { /* fallback to stagger */ }

        function getNextSlot(platform, startDate, slotIndex) {
          const platformRules = rules.filter(r => r.platform?.toLowerCase() === platform?.toLowerCase());
          if (platformRules.length === 0) {
            const d = new Date(startDate.getTime() + slotIndex * 24 * 60 * 60 * 1000);
            d.setHours(10, 0, 0, 0);
            return d.toISOString().slice(0, 16);
          }
          const rule = platformRules[0];
          let cadence = [];
          try { cadence = JSON.parse(rule.cadence_json || '[]'); } catch(e) {}
          if (Array.isArray(cadence) && cadence.length > 0) {
            const dayMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
            const slots = [];
            for (const entry of cadence) {
              const dayNum = dayMap[(entry.day || '').toLowerCase()] ?? -1;
              const times = Array.isArray(entry.times) ? entry.times : [entry.time || '10:00'];
              for (const t of times) { if (dayNum >= 0) slots.push({ dayNum, time: t }); }
            }
            if (slots.length > 0) {
              const cursor = new Date(startDate);
              let found = 0;
              for (let dayOffset = 0; dayOffset < 90; dayOffset++) {
                const d = new Date(cursor.getTime() + dayOffset * 24 * 60 * 60 * 1000);
                const daySlots = slots.filter(s => s.dayNum === d.getDay());
                for (const s of daySlots) {
                  if (found === slotIndex) {
                    const [h, m] = (s.time || '10:00').split(':');
                    d.setHours(parseInt(h) || 10, parseInt(m) || 0, 0, 0);
                    return d.toISOString().slice(0, 16);
                  }
                  found++;
                }
              }
            }
          }
          const d = new Date(startDate.getTime() + slotIndex * 24 * 60 * 60 * 1000);
          d.setHours(10, 0, 0, 0);
          return d.toISOString().slice(0, 16);
        }

        const startDate = this.prefilledScheduleDate
          ? new Date(this.prefilledScheduleDate)
          : new Date(Date.now() + 24 * 60 * 60 * 1000);
        const platformSlotIndex = {};

        for (const item of (this.items || [])) {
          if (!item.text && !item.content) continue;
          const platform = item.platform || 'x';
          if (!platformSlotIndex[platform]) platformSlotIndex[platform] = 0;
          const scheduledAt = item.scheduledAt || getNextSlot(platform, startDate, platformSlotIndex[platform]);
          platformSlotIndex[platform]++;
          await client.saveToLibrary({ items: [{
            platform, content: item.text || item.content, title: item.title || '',
            scheduledAt, status: 'scheduled', type: 'social',
            rootId: this.campaignId, metadata: item.meta || {}, context: item.context || '',
            media: (Array.isArray(item.media) && item.media.length > 0) ? item.media : null
          }]});
          scheduled++;
        }
        this.items = [];
        this.selectedItemId = null;
        this.activeDraft = null;
        await this.loadSocialDrafts();
        this.socialToast = { msg: `${scheduled} posts scheduled!`, kind: 'success',
          actions: [{ label: 'View in calendar', action: 'nav-calendar' }] };
        this.render();
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => { this.socialToast = null; this.render(); }, 5000);
      } catch (err) {
        if (btn) { btn.disabled = false; btn.textContent = 'Schedule All'; }
        this.socialToast = { msg: 'Failed: ' + (err.message || 'Unknown error'), kind: 'error' };
        this.render();
      }
      return;
    }

    // Save All to Library button
    if (e.target.closest('#btn-save-all')) {
      const btn = this.container.querySelector('#btn-save-all');
      if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
      let saved = 0;
      try {
        for (const item of (this.items || [])) {
          if (!item.text && !item.content) continue;
          await client.saveToLibrary({ items: [{
            platform: item.platform, content: item.text || item.content, title: item.title || '',
            status: 'draft', type: 'social',
            rootId: this.campaignId, metadata: item.meta || {}, context: item.context || '',
            media: (Array.isArray(item.media) && item.media.length > 0) ? item.media : null
          }]});
          saved++;
        }
        this.items = [];
        this.selectedItemId = null;
        this.activeDraft = null;
        await this.loadSocialDrafts();
        this.socialToast = { msg: `${saved} drafts saved to library!`, kind: 'success',
          actions: [{ label: 'View in library', action: 'nav-library' }] };
        this.render();
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => { this.socialToast = null; this.render(); }, 5000);
      } catch (err) {
        if (btn) { btn.disabled = false; btn.textContent = 'Save All'; }
        this.socialToast = { msg: 'Failed: ' + (err.message || 'Unknown error'), kind: 'error' };
        this.render();
      }
      return;
    }

    if (e.target.closest('[data-testid="toast-close"]')) {
      this.socialToast = null;
      this.render();
      return;
    }

    // Toast action buttons
    const toastAction = e.target.closest('[data-toast-action]');
    if (toastAction) {
      const action = toastAction.dataset.toastAction;
      this.socialToast = null;
      if (action === 'nav-calendar') window.location.hash = '#calendar';
      else if (action === 'nav-library') window.location.hash = '#library';
      else if (action === 'new-post') this.render();
      else this.render();
      return;
    }

    const copyBtn = e.target.closest('#copy-btn') || e.target.closest('#copy-strip-btn');
    if (copyBtn) {
      let txt = '';
      if (this.selectedItemId) txt = this.items.find(i => i.id === this.selectedItemId)?.text;
      else if (this.activeDraft) txt = this.activeDraft.text;
      if (txt) {
        try { navigator.clipboard.writeText(txt); } catch (_) { }
        this.socialToast = { msg: 'Copied to clipboard', kind: 'success' };
        this.render();
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => { this.socialToast = null; this.render(); }, 2500);
      }
      return;
    }

    if (e.target.closest('[data-testid="strip-clear-all"]')) {
      this.activeDraft = null;
      this.selectedItemId = null;
      this.items = [];
      this.topic = '';
      this.sessionContext = '';
      this.attachedMedia = [];
      this.socialDirty = false;
      this.socialSavedAt = null;
      this.socialScheduledAt = null;
      this.ensureSocialDraftSelected();
      this.render();
      return;
    }

    // Campaign modal open
    if (e.target.closest('[data-testid="strip-create-campaign"]')) {
      this.campaignModalOpen = true;
      this.render();
      return;
    }

    // Campaign modal close
    if (e.target.closest('[data-testid="campaign-modal-cancel"]') || e.target.closest('[data-testid="campaign-modal-close"]')) {
      this.campaignModalOpen = false;
      this.render();
      return;
    }

    // Campaign overlay click to close
    if (e.target.closest('[data-testid="campaign-overlay"]') && e.target.classList.contains('campaign-overlay')) {
      this.campaignModalOpen = false;
      this.render();
      return;
    }

    // Campaign modal platform chips
    if (e.target.closest('[data-modal-platform]')) {
      const chip = e.target.closest('[data-modal-platform]');
      chip.classList.toggle('on');
      chip.classList.toggle('off');
      return;
    }

    // Save campaign & keep editing
    if (e.target.closest('[data-testid="save-campaign-stay"]')) {
      await this._saveCampaignFromModal(false);
      return;
    }

    // Save campaign & go
    if (e.target.closest('[data-testid="save-campaign-go"]')) {
      await this._saveCampaignFromModal(true);
      return;
    }

    if (e.target.id === 'campaign-check') {
      this.campaignMode = e.target.checked;
      this.saveState();
      this.render();
      return;
    }

    if (e.target.name === 'platform') {
      this.platforms[e.target.value] = e.target.checked;
      this.saveState();
      return;
    }

    const pill = e.target.closest('.platform-pill') || e.target.closest('.platform-pill-v3[data-platform]');
    if (pill && !pill.disabled && pill.dataset.platform) {
      const targetPlatform = pill.dataset.platform;

      // Check if an item already exists for this platform
      const existingItem = this.items.find(i => i.platform === targetPlatform);
      if (existingItem) {
        // If clicking the currently selected platform, deselect/remove it
        if (this.selectedItemId === existingItem.id) {
          this.items = this.items.filter(i => i.id !== existingItem.id);
          this.selectedItemId = this.items.length > 0 ? this.items[0].id : null;
          this.previewPlatform = this.items.length > 0 ? this.items[0].platform : 'facebook';
          this.socialDirty = true;
          this.render();
          return;
        }
        // Navigate to the existing item
        this.selectedItemId = existingItem.id;
        this.activeDraft = null;
        this.previewPlatform = targetPlatform;
        this.render();
      } else {
        // No item for this platform — create one (clone content if available)
        const sourceItem = this.activeDraft || this.items.find(i => i.id === this.selectedItemId);
        // Read current editor text in case the model hasn't synced
        const editorEl = this.container?.querySelector('[data-testid="content-editor"]');
        const editorText = editorEl?.value?.trim() || '';
        const newItem = {
          id: this.generateId(),
          platform: targetPlatform,
          text: sourceItem?.text || editorText || '',
          title: sourceItem?.title || '',
          status: 'draft',
          meta: { ...(sourceItem?.meta || {}) }
        };
        this.items.push(newItem);
        this.selectedItemId = newItem.id;
        this.activeDraft = null;
        this.previewPlatform = targetPlatform;
        this.socialDirty = true;
        this.render();
      }
    }
  }

  async expandToPlatforms(mode = 'copy') {
    const sourceItem = this.activeDraft || this.items.find(i => i.id === this.selectedItemId);
    if (!sourceItem || !sourceItem.text) return;

    const allPlatforms = Object.keys(this.platforms).filter(p => this.platforms[p]);
    const existingPlatforms = new Set(this.items.map(i => i.platform));
    const newPlatforms = allPlatforms.filter(p => !existingPlatforms.has(p));

    // If no checked platforms are missing items, expand to ALL checked platforms
    const targetPlatforms = newPlatforms.length > 0 ? newPlatforms : allPlatforms.filter(p => p !== sourceItem.platform);

    if (targetPlatforms.length === 0) return;

    if (mode === 'copy') {
      for (const p of targetPlatforms) {
        if (!this.items.some(i => i.platform === p)) {
          this.items.push({
            id: this.generateId(),
            platform: p,
            text: sourceItem.text,
            title: sourceItem.title || '',
            status: 'draft',
            meta: { ...(sourceItem.meta || {}) }
          });
        }
      }
      this.render();
    } else if (mode === 'ai') {
      this.loading = true;
      this.render();
      try {
        const platforms = targetPlatforms.map(p => ({ platform: p, sessionContext: '' }));
        const res = await client.generateBatch({
          topic: sourceItem.text.substring(0, 500),
          platforms,
          toneOverride: this.getEffectiveTone(),
          postLength: this.postLength
        });
        const drafts = res.drafts || res.items || [];
        for (let idx = 0; idx < targetPlatforms.length; idx++) {
          const p = targetPlatforms[idx];
          if (!this.items.some(i => i.platform === p)) {
            this.items.push({
              id: this.generateId(),
              platform: p,
              text: drafts[idx]?.text || sourceItem.text,
              title: drafts[idx]?.title || sourceItem.title || '',
              status: 'draft',
              meta: { hashtags: drafts[idx]?.hashtags || [] }
            });
          }
        }
      } catch (err) {
        console.error('[EXPAND_AI]', err);
        // Fallback to copy
        for (const p of targetPlatforms) {
          if (!this.items.some(i => i.platform === p)) {
            this.items.push({ id: this.generateId(), platform: p, text: sourceItem.text, title: sourceItem.title || '', status: 'draft', meta: {} });
          }
        }
      }
      this.loading = false;
      this.render();
    }
  }

  handleBlogClick(e) {
    if (e.target.closest('[data-testid="blog-drafts-toggle"]')) {
      this.blogDraftsDrawerOpen = true;
      this.render();
      return;
    }

    if (e.target.closest('[data-testid="blog-drafts-overlay"]') || e.target.closest('[data-testid="blog-drafts-close"]')) {
      this.blogDraftsDrawerOpen = false;
      this.render();
      return;
    }

    if (e.target.closest('[data-testid="blog-drafts-new"]')) {
      this.activeBlogPost = { id: `new-draft-${Date.now()}`, title: '', content_html: '', status: 'draft', template_key: this.blogTemplate, mode: this.blogMode };
      this.blogDirty = false;
      this.blogSavedAt = null;
      this.blogDraftsDrawerOpen = false;
      this.render();
      return;
    }

    const draftItem = e.target.closest('.blog-draft-item');
    if (draftItem) {
      const id = draftItem.dataset.id;
      if (id && id !== 'undefined') {
        this.blogDraftsDrawerOpen = false;
        this.loadBlogPostIntoEditor(id);
      } else {
        console.warn('[BLOG_CLICK_WARNING] Draft item clicked but ID is missing', draftItem);
      }
      return;
    }

    const toolbarBtn = e.target.closest('.blog-toolbar button');
    if (toolbarBtn && !toolbarBtn.classList.contains('toolbar-toggle-btn') && !toolbarBtn.classList.contains('blog-toolbar-help')) {
      const cmd = toolbarBtn.dataset.cmd;
      const val = toolbarBtn.dataset.val;
      if (cmd) { this.handleEditorCommand(cmd, val); return; }
    }

    if (e.target.closest('.blog-generate-btn')) { this.handleBlogGenerate(); return; }
    if (e.target.closest('.blog-save-btn') || e.target.closest('.blog-footer-save')) { this.handleBlogSave(); return; }
    if (e.target.closest('.blog-social-gen-btn')) { this.handleBlogToSocial(); return; }

    // Landing-page preview modal (shows how the post will look on heistbrain.com/blog)
    if (e.target.closest('.blog-landing-preview-btn')) {
      import('../ui/landingBlogPreview.js').then((mod) => {
        const post = { ...(this.activeBlogPost || {}) };
        const editor = this.container.querySelector('.blog-content-editor');
        if (editor) post.content_html = editor.innerHTML;
        const titleInput = this.container.querySelector('.blog-title-input');
        if (titleInput) post.title = titleInput.value;
        post.author = window.__auth__?.name || 'Heist Team';
        // Preview pill = the post's primary (first) tag.
        const firstTag = Array.isArray(this.activeBlogPost?.tags) && this.activeBlogPost.tags.length
          ? this.activeBlogPost.tags[0] : 'product';
        post.category = firstTag;
        post.landing_category = firstTag;
        mod.openLandingPreviewModal(post);
      });
      return;
    }

    // Tag chip — toggle membership in the post's tags array.
    const tagChip = e.target.closest('.blog-tag-chip');
    if (tagChip) {
      const slug = tagChip.getAttribute('data-tag');
      if (slug && this.activeBlogPost) {
        if (!Array.isArray(this.activeBlogPost.tags)) this.activeBlogPost.tags = [];
        const idx = this.activeBlogPost.tags.indexOf(slug);
        if (idx >= 0) this.activeBlogPost.tags.splice(idx, 1);
        else this.activeBlogPost.tags.push(slug);
        this.blogDirty = true;
        this.render();
      }
      return;
    }

    // Add-tag button — create/select a new tag from the text input.
    if (e.target.closest('.blog-tag-add-btn')) {
      this._addBlogTagFromInput();
      return;
    }

    // Brand alignment score — click to see breakdown + apply with AI
    if (e.target.closest('[data-action="open-brand-score-breakdown"]')) {
      this.openBrandScoreBreakdown();
      return;
    }
    // Undo a brand-alignment AI rewrite
    if (e.target.closest('[data-action="undo-brand-alignment"]')) {
      this.undoBrandAlignment();
      return;
    }

    // Blog snippets — new / manage buttons in sidebar
    if (e.target.closest('.blog-snippet-add-btn')) {
      import('../ui/blogSnippetEditor.js').then((mod) => {
        mod.openBlogSnippetEditor({ onSaved: () => this.refreshBlogSnippets() });
      });
      return;
    }
    if (e.target.closest('.blog-snippet-manage-btn')) {
      import('../ui/blogSnippetDrawer.js').then((mod) => {
        mod.openBlogSnippetDrawer();
      });
      return;
    }
    // Toggle a sidebar snippet checkbox (per-post include/exclude)
    const sidebarSnipToggle = e.target.closest('.bs-sidebar-toggle');
    if (sidebarSnipToggle) {
      const id = sidebarSnipToggle.dataset.snippetId;
      if (!this.activeBlogPost) return;
      const disabled = Array.isArray(this.activeBlogPost.disabled_snippet_ids)
        ? [...this.activeBlogPost.disabled_snippet_ids]
        : (() => {
            try { return JSON.parse(this.activeBlogPost.disabled_snippet_ids || '[]'); }
            catch (_) { return []; }
          })();
      const snip = (this.blogSnippets || []).find((s) => s.id === id);
      if (!snip) return;
      // Flip the disabled flag for auto-apply snippets, or the manual include for the rest.
      if (snip.auto_apply) {
        const idx = disabled.indexOf(id);
        if (idx >= 0) disabled.splice(idx, 1); else disabled.push(id);
      } else {
        this.manualSnippetIds = this.manualSnippetIds || new Set();
        if (this.manualSnippetIds.has(id)) this.manualSnippetIds.delete(id);
        else this.manualSnippetIds.add(id);
      }
      this.activeBlogPost.disabled_snippet_ids = disabled;
      this.blogDirty = true;
      this.renderBlogSidebarSnippets();
      this.renderBlogDirtyUI();
      return;
    }

    // WordPress sync button
    if (e.target.closest('.blog-footer-wp-sync')) { this._handleWpSync(); return; }

    // WordPress tag add button
    if (e.target.closest('.blog-wp-tag-add-btn')) { this._handleWpTagAdd(); return; }

    // WordPress tag remove
    const tagRemove = e.target.closest('.blog-wp-tag-remove');
    if (tagRemove) {
      const tagId = parseInt(tagRemove.dataset.tagId, 10);
      if (this.activeBlogPost) {
        let tags = this.activeBlogPost.wp_tags;
        if (typeof tags === 'string') { try { tags = JSON.parse(tags); } catch (_) { tags = []; } }
        if (!Array.isArray(tags)) tags = [];
        this.activeBlogPost.wp_tags = JSON.stringify(tags.filter(id => id !== tagId));
        this.blogDirty = true;
        this.render();
      }
      return;
    }

    // WordPress category checkbox
    const catCheck = e.target.closest('.blog-wp-cat-check');
    if (catCheck) {
      const catId = parseInt(catCheck.dataset.catId, 10);
      if (this.activeBlogPost) {
        let cats = this.activeBlogPost.wp_categories;
        if (typeof cats === 'string') { try { cats = JSON.parse(cats); } catch (_) { cats = []; } }
        if (!Array.isArray(cats)) cats = [];
        if (catCheck.checked) {
          if (!cats.includes(catId)) cats.push(catId);
        } else {
          cats = cats.filter(id => id !== catId);
        }
        this.activeBlogPost.wp_categories = JSON.stringify(cats);
        this.blogDirty = true;
        this.renderBlogDirtyUI();
      }
      return;
    }

    // Footer publish button
    if (e.target.closest('.blog-footer-publish')) { this.handleBlogPublish(); return; }

    // Convert to social toggle dropdown
    if (e.target.closest('.blog-convert-btn')) {
      const dropdown = this.container.querySelector('.blog-convert-dropdown');
      if (dropdown) dropdown.classList.toggle('open');
      return;
    }

    // Convert to social options
    const convertOpt = e.target.closest('.blog-convert-option');
    if (convertOpt) {
      const type = convertOpt.dataset.convert;
      const dropdown = this.container.querySelector('.blog-convert-dropdown');
      if (dropdown) dropdown.classList.remove('open');
      this.handleBlogConvertSocial(type);
      return;
    }

    // Visual/Text View Toggle
    const viewBtn = e.target.closest('.blog-editor-view-toggle button');
    if (viewBtn) {
      const newView = viewBtn.dataset.view;
      if (this.blogEditorView !== newView) {
        this.syncBlogEditorContent(this.blogEditorView, newView);
        this.blogEditorView = newView;
        this.render();
      }
      return;
    }

    // Toolbar Toggle
    const toolbarToggle = e.target.closest('.toolbar-toggle-btn');
    if (toolbarToggle) {
      this.blogToolbarExpanded = !this.blogToolbarExpanded;
      this.render();
      return;
    }

    // Help Tooltip
    if (e.target.closest('.blog-toolbar-help')) {
      this.setBlogNotice("Heist Blog Editor: Use Visual mode for rich formatting, or Text mode for raw HTML editing.");
      return;
    }
  }

  async loadBlogPostIntoEditor(id) { return blog.loadBlogPostIntoEditor(this, id); }

  // Add the tag typed in the picker's text input: slugify, select it on the
  // post, and register it in the brand vocabulary (best-effort) so it gets a
  // color and shows up for other posts.
  _addBlogTagFromInput() {
    const inp = this.container.querySelector('.blog-tag-add-input');
    if (!inp) return;
    const slug = blog.slugifyTag(inp.value);
    inp.value = '';
    if (!slug) return;
    if (!this.activeBlogPost) this.activeBlogPost = { title: '', content_html: '', tags: [] };
    if (!Array.isArray(this.activeBlogPost.tags)) this.activeBlogPost.tags = [];
    if (!this.activeBlogPost.tags.includes(slug)) this.activeBlogPost.tags.push(slug);
    this.blogDirty = true;
    // Register in the brand registry so the chip persists for future posts.
    const known = (this.blogTagRegistry || []).some(t => t.slug === slug);
    if (!known) {
      client.createBlogTag({ slug }).then((res) => {
        if (res && res.ok && res.tag) {
          if (!this.blogTagRegistry) this.blogTagRegistry = [];
          if (!this.blogTagRegistry.some(t => t.slug === res.tag.slug)) {
            this.blogTagRegistry.push(res.tag);
            this.render();
          }
        }
      }).catch(() => { /* still selected on the post; save will auto-register */ });
    }
    this.render();
  }

  syncBlogEditorContent(from, to) { return blog.syncBlogEditorContent(this, from, to); }

  _toggleMediaPanel() {
    var panel = this.container.querySelector('.blog-media-panel');
    if (!panel) return;
    var isOpen = panel.style.display !== 'none';
    panel.style.display = isOpen ? 'none' : '';
    if (!isOpen) this._initMediaPanel(panel);
  }

  _initMediaPanel(panel) {
    var self = this;
    var editor = this.container.querySelector('.blog-content-editor');

    // Tab switching
    var tabs = panel.querySelectorAll('.media-tab');
    for (var i = 0; i < tabs.length; i++) {
      tabs[i].onclick = function() {
        var tabName = this.getAttribute('data-media-tab');
        var allTabs = panel.querySelectorAll('.media-tab');
        var allContents = panel.querySelectorAll('.media-tab-content');
        for (var j = 0; j < allTabs.length; j++) allTabs[j].classList.remove('active');
        for (var j = 0; j < allContents.length; j++) allContents[j].classList.remove('active');
        this.classList.add('active');
        var target = panel.querySelector('[data-tab="' + tabName + '"]');
        if (target) target.classList.add('active');
        if (tabName === 'recent') self._loadRecentImages(panel);
      };
    }

    // Close button
    var closeBtn = panel.querySelector('.media-panel-close');
    if (closeBtn) closeBtn.onclick = function() { panel.style.display = 'none'; };

    // Upload tab — browse button
    var browseBtn = panel.querySelector('.media-browse-link, .media-browse-btn');
    if (browseBtn) {
      browseBtn.onclick = function() {
        var input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        input.onchange = async function() {
          for (var k = 0; k < input.files.length; k++) {
            editor.focus();
            await self._uploadBlogImage(input.files[k], editor);
          }
          panel.style.display = 'none';
        };
        input.click();
      };
    }

    // Upload tab — dropzone
    var dropzone = panel.querySelector('.media-dropzone');
    if (dropzone) {
      dropzone.ondragover = function(e) { e.preventDefault(); dropzone.classList.add('media-dropzone--active'); };
      dropzone.ondragleave = function(e) { e.preventDefault(); dropzone.classList.remove('media-dropzone--active'); };
      dropzone.ondrop = async function(e) {
        e.preventDefault();
        dropzone.classList.remove('media-dropzone--active');
        var files = Array.from(e.dataTransfer.files).filter(function(f) { return f.type.startsWith('image/'); });
        for (var k = 0; k < files.length; k++) {
          editor.focus();
          await self._uploadBlogImage(files[k], editor);
        }
        panel.style.display = 'none';
      };
    }

    // Stock photo tab — search
    var stockBtn = panel.querySelector('.media-stock-btn');
    var stockInput = panel.querySelector('.media-stock-input');
    if (stockBtn && stockInput) {
      var doSearch = async function() {
        var query = stockInput.value.trim();
        if (!query) return;
        var results = panel.querySelector('.media-stock-results');
        results.innerHTML = '<p class="text-muted">Searching...</p>';
        try {
          var mod = await import('../utils/unsplash.js');
          var photos = await mod.searchPhotos(query, 12);
          if (!photos || !photos.length) { results.innerHTML = '<p class="text-muted">No results found</p>'; return; }
          var html = '';
          for (var k = 0; k < photos.length; k++) {
            html += '<div class="media-stock-thumb" data-url="' + photos[k].url + '" data-alt="' + (photos[k].alt || '').replace(/"/g, '&quot;') + '">';
            html += '<img src="' + photos[k].thumb + '" alt="' + (photos[k].alt || '') + '" loading="lazy" />';
            html += '<span class="media-stock-credit">by ' + (photos[k].author || '') + '</span>';
            html += '</div>';
          }
          results.innerHTML = html;
          var thumbs = results.querySelectorAll('.media-stock-thumb');
          for (var k = 0; k < thumbs.length; k++) {
            thumbs[k].onclick = function() {
              editor.focus();
              document.execCommand('insertHTML', false,
                '<figure class="wp-block-image"><img src="' + this.dataset.url + '" alt="' + this.dataset.alt + '" /><figcaption class="wp-caption-text">' + this.dataset.alt + '</figcaption></figure>'
              );
              self.blogDirty = true;
              if (self.activeBlogPost) self.activeBlogPost.content_html = editor.innerHTML;
              self.renderBlogDirtyUI();
              panel.style.display = 'none';
            };
          }
        } catch (err) {
          results.innerHTML = '<p class="text-muted">Search failed: ' + _esc(err.message) + '</p>';
        }
      };
      stockBtn.onclick = doSearch;
      stockInput.onkeydown = function(e) { if (e.key === 'Enter') doSearch(); };
    }

    // Stock VIDEO tab — Pexels video search (proxied via /api/media/stock/videos)
    var videoBtn = panel.querySelector('.media-video-btn');
    var videoInput = panel.querySelector('.media-video-input');
    if (videoBtn && videoInput) {
      var doVideoSearch = async function() {
        var query = videoInput.value.trim();
        if (!query) return;
        var results = panel.querySelector('.media-video-results');
        results.innerHTML = '<p class="text-muted">Searching...</p>';
        try {
          var mod = await import('../utils/pexels.js');
          var videos = await mod.searchStockVideos(query, 12);
          if (!videos || !videos.length) { results.innerHTML = '<p class="text-muted">No videos found</p>'; return; }
          var html = '';
          for (var k = 0; k < videos.length; k++) {
            var v = videos[k];
            var dur = v.duration ? (Math.floor(v.duration / 60) + ':' + String(v.duration % 60).padStart(2, '0')) : '';
            var escUrl = (v.downloadUrl || '').replace(/"/g, '&quot;');
            var escPoster = (v.thumb || '').replace(/"/g, '&quot;');
            var escAuthor = (v.author || '').replace(/"/g, '&quot;');
            html += '<div class="media-video-card">';
            html += '<div class="media-video-player-wrap">';
            html += '<video class="media-video-player" src="' + escUrl + '" poster="' + escPoster + '" controls playsinline preload="metadata"></video>';
            if (dur) html += '<span class="media-video-duration">' + dur + '</span>';
            html += '</div>';
            html += '<div class="media-video-card-footer">';
            html += '<span class="media-stock-credit">by ' + escAuthor + '</span>';
            html += '<button class="btn btn-sm btn-primary media-video-insert" data-url="' + escUrl + '" data-poster="' + escPoster + '" data-author="' + escAuthor + '">Insert</button>';
            html += '</div>';
            html += '</div>';
          }
          results.innerHTML = html;
          var inserts = results.querySelectorAll('.media-video-insert');
          for (var k = 0; k < inserts.length; k++) {
            inserts[k].onclick = function(ev) {
              ev.stopPropagation();
              editor.focus();
              var src = this.dataset.url;
              var poster = this.dataset.poster;
              var author = this.dataset.author || 'Pexels';
              document.execCommand('insertHTML', false,
                '<figure class="wp-block-video"><video src="' + src + '" poster="' + poster + '" controls playsinline preload="metadata" style="max-width:100%;height:auto;"></video><figcaption class="wp-caption-text">Video by ' + author + ' on Pexels</figcaption></figure>'
              );
              self.blogDirty = true;
              if (self.activeBlogPost) self.activeBlogPost.content_html = editor.innerHTML;
              self.renderBlogDirtyUI();
              panel.style.display = 'none';
            };
          }
        } catch (err) {
          var msg = err.code === 'PEXELS_NOT_CONFIGURED'
            ? 'Stock videos not configured. Ask your admin to add PEXELS_API_KEY.'
            : ('Search failed: ' + (err.message || ''));
          results.innerHTML = '<p class="text-muted">' + _esc(msg) + '</p>';
        }
      };
      videoBtn.onclick = doVideoSearch;
      videoInput.onkeydown = function(e) { if (e.key === 'Enter') doVideoSearch(); };
    }

    // URL tab — insert
    var urlInsert = panel.querySelector('.media-url-insert');
    if (urlInsert) {
      urlInsert.onclick = function() {
        var urlVal = panel.querySelector('.media-url-input').value.trim();
        var altVal = panel.querySelector('.media-url-alt').value.trim() || 'Blog image';
        if (!urlVal) return;
        editor.focus();
        document.execCommand('insertHTML', false,
          '<figure class="wp-block-image"><img src="' + urlVal + '" alt="' + altVal + '" /><figcaption class="wp-caption-text"></figcaption></figure>'
        );
        self.blogDirty = true;
        if (self.activeBlogPost) self.activeBlogPost.content_html = editor.innerHTML;
        self.renderBlogDirtyUI();
        panel.style.display = 'none';
      };
    }
  }

  async _loadRecentImages(panel) {
    var self = this;
    var editor = this.container.querySelector('.blog-content-editor');
    var grid = panel.querySelector('.media-recent-grid');
    if (!grid) return;
    grid.innerHTML = '<p class="text-muted">Loading...</p>';
    try {
      var res = await client.getBlogImages();
      if (!res.ok || !res.images || !res.images.length) {
        grid.innerHTML = '<p class="text-muted media-recent-empty">No recent images yet. Upload one to get started.</p>';
        return;
      }
      var html = '';
      for (var i = 0; i < res.images.length; i++) {
        html += '<div class="media-recent-thumb" data-url="' + res.images[i].url + '" data-name="' + res.images[i].name + '">';
        html += '<img src="' + res.images[i].url + '" alt="' + res.images[i].name + '" loading="lazy" />';
        html += '</div>';
      }
      grid.innerHTML = html;
      var thumbs = grid.querySelectorAll('.media-recent-thumb');
      for (var i = 0; i < thumbs.length; i++) {
        thumbs[i].onclick = function() {
          editor.focus();
          var name = this.dataset.name.replace(/\.[^.]+$/, '').replace(/^\d+-/, '');
          document.execCommand('insertHTML', false,
            '<figure class="wp-block-image"><img src="' + this.dataset.url + '" alt="' + name + '" /><figcaption class="wp-caption-text"></figcaption></figure>'
          );
          self.blogDirty = true;
          if (self.activeBlogPost) self.activeBlogPost.content_html = editor.innerHTML;
          self.renderBlogDirtyUI();
          panel.style.display = 'none';
        };
      }
    } catch (err) {
      grid.innerHTML = '<p class="text-muted">Failed to load images</p>';
    }
  }

  async _uploadBlogImage(file, editor) {
    var placeholderId = 'img-upload-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6);
    var localUrl = URL.createObjectURL(file);
    var html = '<figure class="wp-block-image" id="' + placeholderId + '"><img src="' + localUrl + '" alt="Uploading..." class="blog-img-uploading" /><figcaption class="wp-caption-text">Uploading...</figcaption></figure>';
    document.execCommand('insertHTML', false, html);

    try {
      var res = await client.uploadBlogImage(file);
      if (res.ok && res.url) {
        var placeholder = editor.querySelector('#' + placeholderId);
        if (placeholder) {
          var img = placeholder.querySelector('img');
          URL.revokeObjectURL(img.src);
          img.src = res.url;
          img.alt = file.name.replace(/\.[^.]+$/, '');
          img.classList.remove('blog-img-uploading');
          var caption = placeholder.querySelector('figcaption');
          if (caption) caption.textContent = '';
          placeholder.removeAttribute('id');
        }
      } else {
        throw new Error(res.error?.message || 'Upload failed');
      }
    } catch (err) {
      var el = editor.querySelector('#' + placeholderId);
      if (el) el.innerHTML = '<p class="text-danger">Image upload failed: ' + _esc(err.message) + '</p>';
    }

    if (!this.activeBlogPost) this.activeBlogPost = { title: '', content_html: '' };
    this.activeBlogPost.content_html = editor.innerHTML;
    this.blogDirty = true;
    this.renderBlogDirtyUI();
  }

  handleEditorCommand(cmd, val) {
    if (this.blogEditorView !== 'visual') return;

    const editor = this.container.querySelector('.blog-content-editor');
    if (!editor) return;
    editor.focus();

    if (cmd === 'fontName') {
      if (!val) return;
      document.execCommand('fontName', false, val);
    } else if (cmd === 'insertChar') {
      if (!val) return;
      document.execCommand('insertHTML', false, val);
    } else if (cmd === 'insertReadMore') {
      document.execCommand('insertHTML', false, '<hr class="wp-more-tag" data-more="1"/><p><br></p>');
    } else if (cmd === 'insertCodeBlock') {
      document.execCommand('insertHTML', false, '<pre class="wp-code-block"><code>// code here</code></pre><p><br></p>');
    } else if (cmd === 'insertTable') {
      // Show grid picker instead of fixed 3x3
      import('../ui/editor/tableEditor.js').then(({ showGridPicker }) => {
        showGridPicker(editor, (rows, cols) => {
          let html = '<table class="wp-block-table"><tbody>';
          for (let r = 0; r < rows; r++) {
            html += '<tr>';
            for (let c = 0; c < cols; c++) {
              html += `<td>${r === 0 && c === 0 ? 'Cell 1' : '&nbsp;'}</td>`;
            }
            html += '</tr>';
          }
          html += '</tbody></table><p><br></p>';
          editor.focus();
          document.execCommand('insertHTML', false, html);
          this.blogDirty = true;
          if (this.activeBlogPost) this.activeBlogPost.content_html = editor.innerHTML;
          this.renderDirtyUI();
        });
      });
      return;
    } else if (cmd === 'insertColumns') {
      import('../ui/editor/columnLayout.js').then(({ showColumnPicker, insertColumns }) => {
        showColumnPicker(editor, (layout) => {
          insertColumns(layout);
        });
      });
      return;
    } else if (cmd === 'formatPainter') {
      import('../ui/editor/formatPainter.js').then(({ toggle }) => {
        toggle(false);
      });
      return;
    } else if (cmd === 'openMediaPanel') {
      this._toggleMediaPanel();
      return;
    } else if (cmd === 'uploadImage') {
      var fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.onchange = async () => {
        var file = fileInput.files[0];
        if (!file) return;
        await this._uploadBlogImage(file, editor);
      };
      fileInput.click();
      return;
    } else if (cmd === 'insertImageUrl') {
      var url = prompt('Enter image URL:');
      if (url) {
        document.execCommand('insertHTML', false,
          '<figure class="wp-block-image"><img src="' + url + '" alt="Blog image" /><figcaption class="wp-caption-text">Image caption</figcaption></figure>'
        );
        this.blogDirty = true;
      }
      return;
    } else if (cmd === 'insertStockPhoto') {
      import('../ui/unsplashModal.js').then(async ({ openUnsplashModal }) => {
        var result = await openUnsplashModal(this.topic || '');
        if (result) {
          editor.focus();
          document.execCommand('insertHTML', false,
            '<figure class="wp-block-image"><img src="' + result.url + '" alt="' + result.alt + '" /><figcaption class="wp-caption-text">' + result.alt + ' — via Unsplash</figcaption></figure>'
          );
          this.blogDirty = true;
          if (this.activeBlogPost) {
            this.activeBlogPost.content_html = editor.innerHTML;
          }
          this.renderDirtyUI();
        }
      });
      return;
    } else if (cmd === 'createLink') {
      const url = prompt("Enter the URL:");
      if (url) document.execCommand(cmd, false, url);
    } else if (['insertImage', 'insertVideo'].includes(cmd)) {
      this.showInsertPopover(cmd);
      return;
    } else {
      document.execCommand(cmd, false, val || null);
    }

    this.blogDirty = true;
    if (this.activeBlogPost) {
      this.activeBlogPost.content_html = editor.innerHTML;
    }
    this.renderDirtyUI();
  }

  showInsertPopover(cmd) {
    const toolbar = this.container.querySelector('.blog-toolbar');
    if (!toolbar) return;

    // Remove existing
    const existing = toolbar.querySelector('.blog-insert-popover');
    if (existing) existing.remove();

    const labels = { createLink: 'Link URL', insertImage: 'Image URL', insertVideo: 'Video URL' };
    const placeholders = { createLink: 'https://...', insertImage: 'https://...', insertVideo: 'YouTube/Vimeo URL...' };

    const popover = document.createElement('div');
    popover.className = 'blog-insert-popover';
    popover.innerHTML = `
      <div class="popover-inner">
        <label>${labels[cmd]}</label>
        <input type="text" placeholder="${placeholders[cmd]}" class="popover-input" />
        <div class="popover-actions">
          <button class="popover-btn popover-btn--primary">Insert</button>
          <button class="popover-btn popover-btn--cancel">Cancel</button>
        </div>
      </div>
      `;

    toolbar.appendChild(popover);
    const input = popover.querySelector('input');
    input.focus();

    popover.querySelector('.popover-btn--cancel').onclick = () => popover.remove();
    popover.querySelector('.popover-btn--primary').onclick = () => {
      const val = input.value.trim();
      if (val) {
        if (cmd === 'createLink') document.execCommand(cmd, false, val);
        else if (cmd === 'insertImage') document.execCommand(cmd, false, val);
        else if (cmd === 'insertVideo') this.insertVideoElement(val);

        this.blogDirty = true;
        const editor = this.container.querySelector('.blog-content-editor');
        if (this.activeBlogPost && editor) {
          this.activeBlogPost.content_html = editor.innerHTML;
        }
        this.renderDirtyUI();
      }
      popover.remove();
    };

    // Enter to submit
    input.onkeydown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        popover.querySelector('.popover-btn--primary').click();
      } else if (e.key === 'Escape') {
        popover.remove();
      }
    };
  }

  insertVideoElement(url) {
    let embedUrl = url;
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const id = url.includes('v=') ? url.split('v=')[1].split('&')[0] : url.split('/').pop();
      embedUrl = `https://www.youtube.com/embed/${id}`;
    } else if (url.includes('vimeo.com')) {
      const id = url.split('/').pop();
      embedUrl = `https://player.vimeo.com/video/${id}`;
    }
    const html = `<div class="blog-video-wrapper"><iframe src="${embedUrl}" title="video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div><p><br></p>`;
    document.execCommand('insertHTML', false, html);
  }

  bindSocialInputs() { return social.bindSocialInputs(this); }

  bindBlogInputs() {
    const titleInp = this.container.querySelector('.blog-title-input');
    if (titleInp) titleInp.oninput = (e) => {
      if (!this.activeBlogPost) this.activeBlogPost = { title: '', content_html: '' };
      this.activeBlogPost.title = e.target.value;
      this.blogDirty = true;
      this.renderBlogDirtyUI();
      this.renderBlogMetricsUI();
    };

    const editor = this.container.querySelector('.blog-content-editor');
    if (editor) {
      editor.oninput = (e) => {
        if (!this.activeBlogPost) this.activeBlogPost = { title: '', content_html: '' };
        this.activeBlogPost.content_html = editor.innerHTML;
        this.blogDirty = true;
        this.renderBlogDirtyUI();
        this.renderBlogMetricsUI();
      };

      // Polished "Related Reading" cards write bare `/blog/{slug}` hrefs
      // (the public renderer rewrites them at request time, but the editor
      // doesn't run that rewrite). Resolve the slug to one of this brand's
      // existing posts and navigate to that draft in the editor. Falls
      // back to preventDefault when the slug doesn't match anything (so
      // dead links can't 404 the user).
      const resolveRelatedClick = (e) => {
        const card = e.target.closest('a.related-card');
        if (!card) return;
        e.preventDefault();
        e.stopPropagation();
        const href = card.getAttribute('href') || '';
        // Match `/blog/{slug}` (anchored — don't grab cross-domain links)
        const m = /^\/blog\/([^/?#]+)/.exec(href);
        const slug = m && m[1];
        if (!slug) return;
        // Find the matching post in this brand's library. blogPosts is
        // populated at mount; if it's empty (e.g. user hasn't loaded yet)
        // just no-op rather than fire a wrong navigation.
        const match = (this.blogPosts || []).find(p =>
          p && (p.slug === slug || String(p.id) === String(slug))
        );
        if (match && match.id) {
          window.location.hash = '#create?mode=blog&blogId=' + encodeURIComponent(match.id);
        }
        // No match: nothing happens (we already prevented default).
      };
      editor.addEventListener('click', resolveRelatedClick);
      editor.addEventListener('auxclick', resolveRelatedClick);

      // Paste Interceptor — preserve basic formatting, strip Word/Docs garbage
      editor.onpaste = (e) => {
        e.preventDefault();
        const html = e.clipboardData.getData('text/html');
        const text = e.clipboardData.getData('text/plain');
        if (html) {
          // Strip Word/Docs garbage but keep basic formatting
          const temp = document.createElement('div');
          temp.innerHTML = html;
          // Remove Word/Docs specific elements
          temp.querySelectorAll('meta, link, style, script, xml, o\\:p, w\\:wrap').forEach(el => el.remove());
          // Remove class names from Word (Mso*)
          temp.querySelectorAll('[class*="Mso"]').forEach(el => el.removeAttribute('class'));
          // Remove empty spans
          temp.querySelectorAll('span:empty').forEach(el => el.remove());
          // Clean up the HTML
          const clean = temp.innerHTML
            .replace(/<!--[\s\S]*?-->/g, '')  // comments
            .replace(/<\/?o:[^>]*>/g, '')     // Office namespace tags
            .replace(/<\/?w:[^>]*>/g, '')
            .replace(/<\/?m:[^>]*>/g, '')
            .replace(/\s*mso-[^:]+:[^;"]+;?/gi, '')  // mso-* styles
            .replace(/\s*class=""/g, '');
          document.execCommand('insertHTML', false, clean);
        } else {
          document.execCommand('insertText', false, text);
        }
      };

      // Initialize editor modules (image resize, table editor, columns, format painter)
      import('../ui/editor/imageResize.js').then(m => m.init(editor, this));
      import('../ui/editor/tableEditor.js').then(m => m.init(editor, this));
      import('../ui/editor/columnLayout.js').then(m => m.init(editor, this));
      import('../ui/editor/formatPainter.js').then(m => m.init(editor, this));

      // Drag & Drop — upload images to server
      editor.ondragover = (e) => { e.preventDefault(); editor.classList.add('blog-editor--dragover'); };
      editor.ondragleave = (e) => { e.preventDefault(); editor.classList.remove('blog-editor--dragover'); };
      editor.ondrop = async (e) => {
        e.preventDefault();
        editor.classList.remove('blog-editor--dragover');
        var files = Array.from(e.dataTransfer.files);
        var imageFiles = files.filter(function(f) { return f.type.startsWith('image/'); });
        // Place cursor at drop position
        var range = document.caretRangeFromPoint ? document.caretRangeFromPoint(e.clientX, e.clientY) : null;
        if (range) {
          var sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        }
        for (var i = 0; i < imageFiles.length; i++) {
          await this._uploadBlogImage(imageFiles[i], editor);
        }
      };
    }

    const htmlEditor = this.container.querySelector('.blog-html-editor');
    if (htmlEditor) {
      htmlEditor.oninput = (e) => {
        if (!this.activeBlogPost) this.activeBlogPost = { title: '', content_html: '' };
        this.activeBlogPost.content_html = e.target.value;
        this.blogDirty = true;
        this.renderBlogDirtyUI();
        this.renderBlogMetricsUI();
      };
    }

    const toolbarSelects = this.container.querySelectorAll('.toolbar-select, .toolbar-color-picker');
    toolbarSelects.forEach(sel => {
      sel.onchange = (e) => {
        const cmd = sel.dataset.cmd;
        const val = e.target.value;
        this.handleEditorCommand(cmd, val);
        if (sel.classList.contains('toolbar-select--chars')) sel.value = ""; // Reset Ω select
      };
    });

    const topicInp = this.container.querySelector('.blog-topic-input');
    if (topicInp) topicInp.oninput = (e) => this.topic = e.target.value;

    const ctxInp = this.container.querySelector('.blog-context-input');
    if (ctxInp) ctxInp.oninput = (e) => this.sessionContext = e.target.value;

    const scheduleInp = this.container.querySelector('.blog-schedule-input');
    if (scheduleInp) scheduleInp.onchange = (e) => {
      if (this.activeBlogPost) {
        this.activeBlogPost.scheduled_at = e.target.value ? new Date(e.target.value).toISOString() : null;
        this.blogDirty = true;
        this.renderBlogDirtyUI();
      }
    };

    const sectionPromptInp = this.container.querySelector('.blog-section-prompt');
    if (sectionPromptInp) sectionPromptInp.oninput = (e) => this.blogSectionPrompt = e.target.value;

    // Blog mode toggle (segmented control)
    const modeToggle = this.container.querySelector('.blog-mode-toggle');
    if (modeToggle) modeToggle.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-blog-mode]');
      if (!btn) return;
      this.blogMode = btn.dataset.blogMode;
      this.render();
    });

    const tempSel = this.container.querySelector('.blog-template-select');
    if (tempSel) tempSel.onchange = (e) => this.handleTemplateChange(e.target.value);

    // Phase 37 Commit 7 — "Also produce a faceless video version" checkbox
    const incVideo = this.container.querySelector('#blog-include-video-check');
    if (incVideo) {
      incVideo.onchange = (e) => { this.blogIncludeVideo = !!e.target.checked; };
    }

    // Add-tag input — Enter submits the same path as the Add button.
    const _tagAddInp = this.container.querySelector('.blog-tag-add-input');
    if (_tagAddInp) {
      _tagAddInp.onkeydown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this._addBlogTagFromInput(); }
      };
    }

    // Featured image — shared upload helper
    const _featuredUpload = async (file) => {
      if (!file || !file.type.startsWith('image/')) return;
      if (!this.activeBlogPost) this.activeBlogPost = { title: '', content_html: '' };
      const tempUrl = URL.createObjectURL(file);
      this.activeBlogPost.featured_image_url = tempUrl;
      this.blogDirty = true;
      this.render();
      try {
        const res = await client.uploadBlogImage(file);
        if (res?.ok && res.url) {
          this.activeBlogPost.featured_image_url = res.url;
          URL.revokeObjectURL(tempUrl);
          this.blogDirty = true;
          this.setBlogNotice('Featured image uploaded.');
          this.render();
        } else {
          this.activeBlogPost.featured_image_url = '';
          URL.revokeObjectURL(tempUrl);
          this.setBlogError(res?.error?.message || 'Upload failed.');
          this.render();
        }
      } catch (err) {
        this.activeBlogPost.featured_image_url = '';
        URL.revokeObjectURL(tempUrl);
        this.setBlogError('Upload failed: ' + err.message);
        this.render();
      }
    };

    // Featured image — file upload (click to browse)
    const _featuredFile = this.container.querySelector('.blog-featured-file');
    if (_featuredFile) _featuredFile.onchange = (e) => _featuredUpload(e.target.files?.[0]);

    // Featured image — drag & drop onto dropzone
    const _featuredZone = this.container.querySelector('.blog-featured-dropzone');
    if (_featuredZone) {
      _featuredZone.addEventListener('dragover', (e) => {
        e.preventDefault();
        _featuredZone.style.borderColor = 'var(--primary)';
        _featuredZone.style.background = 'rgba(124,58,237,0.08)';
      });
      _featuredZone.addEventListener('dragleave', () => {
        _featuredZone.style.borderColor = '';
        _featuredZone.style.background = '';
      });
      _featuredZone.addEventListener('drop', (e) => {
        e.preventDefault();
        _featuredZone.style.borderColor = '';
        _featuredZone.style.background = '';
        const file = e.dataTransfer?.files?.[0];
        if (file) _featuredUpload(file);
      });
    }

    // Featured image — browse library
    const _featuredLib = this.container.querySelector('.blog-featured-library-btn');
    if (_featuredLib) _featuredLib.onclick = () => this._openFeaturedImageLibrary();

    // Phase 38 Commit 1 — Featured image: Generate with AI.
    // Derives the prompt from the blog title + first 200 chars of content
    // (or the topic field as fallback). DALL-E 3 size: 1792x1024 (16:9).
    const _featuredAi = this.container.querySelector('.blog-featured-ai-btn');
    if (_featuredAi) {
      _featuredAi.onclick = async () => {
        if (this.blogFeaturedAiGenerating) return;
        const post = this.activeBlogPost || (this.activeBlogPost = { title: '', content_html: '' });
        const title = (post.title || '').trim();
        const plain = (post.content_html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        const promptBase = title || (this.topic || '').trim();
        if (!promptBase) {
          this.blogFeaturedAiError = 'Add a title or topic first — the AI needs something to draw from.';
          this.render();
          return;
        }
        const prompt = `Editorial-style featured image for a blog post titled "${promptBase}". ${plain.slice(0, 240)}`.trim();
        this.blogFeaturedAiGenerating = true;
        this.blogFeaturedAiError = null;
        this.render();
        try {
          const res = await client.generateAiImage({ prompt, size: '1792x1024' });
          const url = res?.url || res?.media?.url || res?.media?.dataUrl;
          if (res?.ok !== false && url) {
            post.featured_image_url = url;
            this.blogDirty = true;
          } else {
            this.blogFeaturedAiError = res?.error?.message || res?.message || 'Could not generate image. Connect an OpenAI key in Settings → AI.';
          }
        } catch (err) {
          const body = err?.body || {};
          if (body.code === 'NO_IMAGE_PROVIDER') {
            this.blogFeaturedAiError = 'Connect an OpenAI / Google AI key in Settings → AI Settings to enable image generation.';
          } else {
            this.blogFeaturedAiError = body.error?.message || body.message || err.message || 'Image generation failed.';
          }
        } finally {
          this.blogFeaturedAiGenerating = false;
          this.render();
        }
      };
    }

    // Featured image — URL paste
    const _featuredUrl = this.container.querySelector('.blog-featured-url');
    if (_featuredUrl) {
      let urlDebounce = null;
      _featuredUrl.oninput = (e) => {
        if (!this.activeBlogPost) this.activeBlogPost = { title: '', content_html: '' };
        this.activeBlogPost.featured_image_url = e.target.value.trim();
        this.blogDirty = true;
        clearTimeout(urlDebounce);
        // Debounce re-render so preview updates as user types without jank
        urlDebounce = setTimeout(() => {
          this.render();
        }, 400);
      };
    }

    // Featured image — remove
    const _featuredRemove = this.container.querySelector('.blog-featured-remove');
    if (_featuredRemove) {
      _featuredRemove.onclick = () => {
        if (!this.activeBlogPost) return;
        this.activeBlogPost.featured_image_url = '';
        this.blogDirty = true;
        this.render();
      };
    }

    // WordPress field bindings
    const wpTagInput = this.container.querySelector('.blog-wp-tag-input');
    if (wpTagInput) wpTagInput.onkeydown = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this._handleWpTagAdd(); }
    };

    const wpStatusSel = this.container.querySelector('.blog-wp-status-select');
    if (wpStatusSel) wpStatusSel.onchange = (e) => {
      if (this.activeBlogPost) {
        this.activeBlogPost.wp_status = e.target.value;
        this.blogDirty = true;
        this.renderBlogDirtyUI();
      }
    };

    const excerptInp = this.container.querySelector('.blog-excerpt-input');
    if (excerptInp) excerptInp.oninput = (e) => {
      if (this.activeBlogPost) {
        this.activeBlogPost.custom_excerpt = e.target.value;
        this.blogDirty = true;
        this.renderBlogDirtyUI();
      }
    };

    // Blog platform selector
    const platformSel = this.container.querySelector('.blog-platform-select');
    if (platformSel) platformSel.onchange = (e) => {
      this.blogPublishPlatform = e.target.value;
      if (this.activeBlogPost) {
        this.activeBlogPost.publish_platform = e.target.value;
        this.blogDirty = true;
      }
      this.render(); // Re-render to show/hide platform-specific settings
    };

    // Meta description for Website platform
    const metaDescInp = this.container.querySelector('.blog-meta-description-input');
    if (metaDescInp) metaDescInp.oninput = (e) => {
      if (this.activeBlogPost) {
        this.activeBlogPost.meta_description = e.target.value;
        this.blogDirty = true;
        this.renderBlogDirtyUI();
      }
    };

    const draftSearch = this.container.querySelector('.blog-drafts-search');
    if (draftSearch) draftSearch.oninput = (e) => {
      this.blogDraftQuery = e.target.value.toLowerCase();
      this.renderDraftsListOnly();
    };

    // Blog autosave: backup to localStorage every 30s when dirty, with indicator
    if (this._blogAutosaveTimer) clearInterval(this._blogAutosaveTimer);
    this._blogAutosaveTimer = setInterval(() => {
      if (!this.blogDirty || !this.activeBlogPost) return;
      const indicator = this.container.querySelector('#autosave-indicator');
      try {
        localStorage.setItem('heist_autosave_blog', JSON.stringify({
          title: this.activeBlogPost.title || '',
          content_html: this.activeBlogPost.content_html || '',
          id: this.activeBlogPost.id || null,
          timestamp: Date.now()
        }));
        if (indicator) {
          indicator.textContent = 'Draft backed up';
          indicator.className = 'autosave-indicator autosave-indicator--saved';
          setTimeout(() => { if (indicator) indicator.className = 'autosave-indicator'; }, 3000);
        }
      } catch(e) {
        if (indicator) { indicator.textContent = 'Backup failed'; indicator.className = 'autosave-indicator autosave-indicator--error'; }
      }
    }, 30000);
  }

  renderDraftsListOnly() {
    const list = this.container.querySelector('.blog-drafts-list');
    if (list) list.innerHTML = this.renderBlogDraftItems();
  }

  renderBlogDraftItems() { return blog.renderBlogDraftItems(this); }


  async loadBlogPosts() { return blog.loadBlogPosts(this); }

  _openFeaturedImageLibrary() {
    // Reuse the shared media library modal (same used by Content Studio,
    // Calendar, Library drawer, Campaigns). It checks for an onMediaPick
    // callback and short-circuits the other handler paths.
    this.onMediaPick = (media) => {
      if (!this.activeBlogPost) this.activeBlogPost = { title: '', content_html: '' };
      const url = media?.dataUrl || media?.url || media?.src || media?.thumb_url || '';
      if (!url) { this.onMediaPick = null; return; }
      this.activeBlogPost.featured_image_url = url;
      this.blogDirty = true;
      this.setBlogNotice('Featured image set from library.');
      this.onMediaPick = null;
      this.render();
    };
    social.openMediaLibrary(this);
    // Also clear the callback if the modal closes without a pick (click backdrop / close button)
    const checkClose = setInterval(() => {
      if (!document.querySelector('.ml-overlay')) {
        this.onMediaPick = null;
        clearInterval(checkClose);
      }
    }, 300);
    // Safety: clear interval after 2 min regardless
    setTimeout(() => clearInterval(checkClose), 120000);
  }

  async _loadWpConnection() {
    try {
      const res = await client.getWpCategories();
      if (res.ok) {
        this.wpConnected = true;
        this.wpCategories = res.categories || [];
        const tagRes = await client.getWpTags();
        this.wpTags = (tagRes.ok ? tagRes.tags : []) || [];
      } else {
        this.wpConnected = false;
        this.wpCategories = [];
        this.wpTags = [];
      }
    } catch (_) {
      this.wpConnected = false;
      this.wpCategories = [];
      this.wpTags = [];
    }
    // Also check website integration status
    try {
      const intRes = await fetch('/api/integrations', {
        headers: { 'x-csrf-token': (document.cookie.split('; ').find(c => c.startsWith('csrf_token='))?.split('=')[1] || '') }
      });
      const intData = await intRes.json();
      // GET /api/integrations returns { ok, items: [...] } — not 'integrations'
      const integrations = intData.items || intData.integrations || [];
      this.websiteConnected = integrations.some(i => i.provider === 'website' && i.status === 'connected');
    } catch (_) {
      this.websiteConnected = false;
    }
    // Set initial platform from active post
    if (!this.blogPublishPlatform && this.activeBlogPost?.publish_platform) {
      this.blogPublishPlatform = this.activeBlogPost.publish_platform;
    }
    this.render();
  }

  async _handleWpTagAdd() {
    const input = this.container.querySelector('.blog-wp-tag-input');
    if (!input || !input.value.trim()) return;
    const name = input.value.trim();

    // Find existing tag by name
    let tag = (this.wpTags || []).find(t => t.name.toLowerCase() === name.toLowerCase());
    if (!tag) {
      // Create new tag on WP
      try {
        const res = await client.createWpTag(name);
        if (res.ok && res.tag) {
          tag = res.tag;
          this.wpTags = [...(this.wpTags || []), tag];
        } else {
          this.setBlogError('Failed to create tag: ' + (res.error?.message || 'Unknown error'));
          return;
        }
      } catch (err) {
        this.setBlogError('Failed to create tag.');
        return;
      }
    }

    // Add tag ID to post's wp_tags
    if (!this.activeBlogPost) return;
    let currentTags = this.activeBlogPost.wp_tags;
    if (typeof currentTags === 'string') {
      try { currentTags = JSON.parse(currentTags); } catch (_) { currentTags = []; }
    }
    if (!Array.isArray(currentTags)) currentTags = [];
    if (!currentTags.includes(tag.id)) {
      currentTags.push(tag.id);
      this.activeBlogPost.wp_tags = JSON.stringify(currentTags);
      this.blogDirty = true;
    }
    input.value = '';
    this.render();
  }

  async _handleWpSync() {
    if (!this.activeBlogPost?.id || !this.activeBlogPost?.wp_post_id) return;
    if (this.blogDirty) {
      this.setBlogError('Save draft before syncing to WordPress.');
      return;
    }
    const btn = this.container.querySelector('.blog-footer-wp-sync');
    if (btn) { btn.disabled = true; btn.textContent = 'Syncing...'; }
    try {
      const res = await client.syncBlogToWp(this.activeBlogPost.id);
      if (res.ok) {
        this.setBlogNotice('Updated on WordPress.' + (res.external_url ? ` ${res.external_url}` : ''));
      } else {
        this.setBlogError(res.error?.message || 'Sync to WordPress failed.');
        // If WP post no longer exists, clear wp_post_id
        if (res.error?.code === 'WP_POST_NOT_FOUND') {
          this.activeBlogPost.wp_post_id = null;
        }
      }
    } catch (err) {
      this.setBlogError('Sync to WordPress failed.');
    }
    this.render();
  }

  async handleBlogGenerate() { return blog.handleBlogGenerate(this); }

  async handleGenerateBlogFromSocial(topic) {
    // Show generating state on the button
    const btn = this.container.querySelector('#btn-generate-blog');
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner" style="width:14px;height:14px;border-width:2px"></span> Generating...';
    }

    try {
      const res = await client.generateBlogDraft({
        mode: 'ai-complete',
        template_key: this.blogTemplate || 'how_to_guide',
        topic: topic,
        sessionContext: this.sessionContext || ''
      });

      if (res.ok) {
        const draft = res.draft || res;
        if (draft.brandScore != null) {
          this.blogBrandScore = draft.brandScore;
        }
        if (Array.isArray(draft.brandChecks)) {
          this.blogBrandChecks = draft.brandChecks;
        }
        // Save the blog post
        this.activeBlogPost = {
          title: draft.title || topic,
          content_html: draft.content_html || '',
          status: 'draft'
        };
        this.blogDirty = true;

        // Try to save it immediately
        try {
          const saveRes = await client.saveBlogPost(this.activeBlogPost);
          if (saveRes && saveRes.ok && saveRes.post) {
            this.activeBlogPost = saveRes.post;
            this.blogDirty = false;
            this.blogSavedAt = new Date().toISOString();
          }
        } catch (saveErr) {
          console.warn('[BLOG_SAVE_AFTER_GENERATE]', saveErr);
        }

        // Show success toast with navigation
        this.showBlogGeneratedToast(this.activeBlogPost.title || topic);
      } else {
        this.showBlogGeneratedToast(null, res.error || 'Generation failed.');
      }
    } catch (err) {
      console.error('[GENERATE_BLOG_FROM_SOCIAL]', err);
      this.showBlogGeneratedToast(null, 'Failed to generate blog. Check server connection.');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"/></svg> Generate Blog';
      }
    }
  }

  showBlogGeneratedToast(title, error) {
    // Remove any existing toast
    const existing = document.querySelector('.blog-generated-toast');
    if (existing) existing.remove();

    const overlays = document.getElementById('overlays') || document.body;

    if (error) {
      const toast = document.createElement('div');
      toast.className = 'blog-generated-toast';
      toast.style.borderColor = '#EF4444';
      toast.innerHTML = `
        <div class="toast-icon" style="background:rgba(239,68,68,0.15);color:#EF4444">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        </div>
        <div class="toast-body">
          <div class="toast-title">Blog Generation Failed</div>
          <div class="toast-desc">${error}</div>
        </div>
        <div class="toast-actions">
          <button class="toast-dismiss-btn" onclick="this.closest('.blog-generated-toast').remove()">Dismiss</button>
        </div>
      `;
      overlays.style.pointerEvents = 'auto';
      overlays.appendChild(toast);
      setTimeout(() => { toast.remove(); overlays.style.pointerEvents = 'none'; }, 6000);
      return;
    }

    const toast = document.createElement('div');
    toast.className = 'blog-generated-toast';
    toast.innerHTML = `
      <div class="toast-icon">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
      </div>
      <div class="toast-body">
        <div class="toast-title">Blog Post Generated</div>
        <div class="toast-desc">${title ? `"${title}" is ready to edit` : 'Your blog post is ready'}</div>
      </div>
      <div class="toast-actions">
        <button class="toast-go-btn" id="toast-go-to-blog">Open in Editor</button>
        <button class="toast-dismiss-btn" id="toast-dismiss-blog">Later</button>
      </div>
    `;

    overlays.style.pointerEvents = 'auto';
    overlays.appendChild(toast);

    // Navigate to blog editor
    toast.querySelector('#toast-go-to-blog').addEventListener('click', () => {
      this.activeCreateMode = 'blog';
      localStorage.setItem('activeCreateMode', 'blog');
      toast.remove();
      overlays.style.pointerEvents = 'none';
      this.loadBlogPosts().then(() => this.render());
    });

    // Dismiss
    toast.querySelector('#toast-dismiss-blog').addEventListener('click', () => {
      toast.remove();
      overlays.style.pointerEvents = 'none';
    });

    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      if (toast.parentElement) {
        toast.remove();
        overlays.style.pointerEvents = 'none';
      }
    }, 10000);
  }

  async handleBlogSave() { return blog.handleBlogSave(this); }

  async handleBlogToSocial() { return blog.handleBlogToSocial(this); }

  // ── Brand alignment breakdown + AI apply + undo ────────────────────────
  async openBrandScoreBreakdown() {
    if (!this.activeBlogPost) return;
    // Always re-score before opening so the breakdown reflects unsaved edits.
    const post = this.activeBlogPost;
    let score = this.blogBrandScore;
    let checks = this.blogBrandChecks || [];
    try {
      const res = await client.scoreBlogPost({
        title: post.title || '',
        content_html: post.content_html || '',
      });
      if (res?.ok) {
        score = res.score;
        checks = res.checks || [];
        this.blogBrandScore = score;
        this.blogBrandChecks = checks;
      }
    } catch (_) {
      // Fall back to the last known score
    }
    const mod = await import('../ui/blogBrandScoreModal.js');
    mod.openBrandScoreModal({
      score,
      checks,
      post: { title: post.title, content_html: post.content_html },
      onApplied: (draft) => this.applyBrandAlignmentDraft(draft),
    });
  }

  applyBrandAlignmentDraft(draft) {
    if (!this.activeBlogPost) return;
    // Snapshot the pre-rewrite state so the user can undo.
    this.blogAlignmentUndo = {
      title: this.activeBlogPost.title,
      content_html: this.activeBlogPost.content_html,
      brandScore: this.blogBrandScore,
      brandChecks: this.blogBrandChecks,
      at: Date.now(),
    };
    // Apply the rewrite
    this.activeBlogPost.title = draft.title || this.activeBlogPost.title;
    this.activeBlogPost.content_html = draft.content_html || this.activeBlogPost.content_html;
    if (draft.brandScore != null) this.blogBrandScore = draft.brandScore;
    if (Array.isArray(draft.brandChecks)) this.blogBrandChecks = draft.brandChecks;
    this.blogDirty = true;
    this.render();
    this.showAlignmentUndoToast(draft);
  }

  undoBrandAlignment() {
    const undo = this.blogAlignmentUndo;
    if (!undo || !this.activeBlogPost) return;
    this.activeBlogPost.title = undo.title;
    this.activeBlogPost.content_html = undo.content_html;
    this.blogBrandScore = undo.brandScore;
    this.blogBrandChecks = undo.brandChecks;
    this.blogAlignmentUndo = null;
    this.blogDirty = true;
    this.render();
    // Remove the toast if it's still up
    const t = document.getElementById('blog-alignment-undo-toast');
    if (t) t.remove();
    import('../ui/toast.js').then((mod) => mod.toast('success', 'Brand alignment rewrite reverted'));
  }

  showAlignmentUndoToast(draft) {
    // Remove any existing alignment toast first
    const existing = document.getElementById('blog-alignment-undo-toast');
    if (existing) existing.remove();

    const overlays = document.getElementById('overlays');
    if (!overlays) return;
    const newScore = draft?.brandScore;
    const toastEl = document.createElement('div');
    toastEl.id = 'blog-alignment-undo-toast';
    toastEl.className = 'toast-banner blog-alignment-toast';
    toastEl.innerHTML = `
      <div class="blog-alignment-toast-inner">
        <span class="blog-alignment-toast-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
        </span>
        <div class="blog-alignment-toast-text">
          <div class="blog-alignment-toast-title">Brand alignment applied${newScore != null ? ` · now ${newScore}%` : ''}</div>
          <div class="blog-alignment-toast-sub">Don't like the rewrite? Undo to restore the previous version.</div>
        </div>
        <button class="blog-alignment-toast-btn" data-action="undo-brand-alignment" type="button">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Undo
        </button>
        <button class="blog-alignment-toast-close" data-action="dismiss-alignment-toast" type="button" aria-label="Dismiss">&times;</button>
      </div>
    `;
    overlays.style.pointerEvents = 'auto';
    overlays.appendChild(toastEl);
    // Auto-dismiss after 20 seconds (generous so the user has time to read + undo)
    setTimeout(() => {
      if (toastEl.parentElement) {
        toastEl.remove();
        overlays.style.pointerEvents = 'none';
      }
    }, 20000);
    // Close handler
    toastEl.querySelector('[data-action="dismiss-alignment-toast"]')?.addEventListener('click', () => {
      toastEl.remove();
      overlays.style.pointerEvents = 'none';
    });
  }

  // ── Blog snippets sidebar wiring ───────────────────────────────────────
  async refreshBlogSnippets() {
    try {
      const res = await client.listBlogSnippets();
      this.blogSnippets = res?.snippets || [];
      this.renderBlogSidebarSnippets();
    } catch (err) {
      this.blogSnippets = [];
      this.renderBlogSidebarSnippets();
    }
  }

  renderBlogSidebarSnippets() {
    const list = this.container?.querySelector('#blog-snippets-sidebar-list');
    if (!list) return;
    const snippets = this.blogSnippets || [];
    if (snippets.length === 0) {
      list.innerHTML = '<div class="bs-sidebar-empty">No snippets yet. Create one to inject reusable HTML into every post.</div>';
      return;
    }
    // Parse per-post disabled list
    let disabled = [];
    try {
      const raw = this.activeBlogPost?.disabled_snippet_ids;
      disabled = Array.isArray(raw) ? raw : JSON.parse(raw || '[]');
    } catch (_) { disabled = []; }
    const manualIncluded = this.manualSnippetIds || new Set();

    list.innerHTML = snippets.map((s) => {
      const scope = (s.scope === 'global' || !s.brand_id) ? '🌐' : '🎨';
      const auto = s.auto_apply ? 'auto' : 'manual';
      const pos = ({end:'end',start:'start',before_cta:'before CTA',replace_cta:'replace CTA',after_first_h2:'after H2'})[s.position] || s.position;
      let checked;
      if (s.auto_apply) checked = !disabled.includes(s.id);
      else checked = manualIncluded.has(s.id);
      const nameSafe = s.name.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `
        <label class="bs-sidebar-row">
          <input type="checkbox" class="bs-sidebar-toggle" data-snippet-id="${s.id}" ${checked ? 'checked' : ''}>
          <span class="bs-sidebar-name">${nameSafe}</span>
          <span class="bs-sidebar-meta">${scope} · ${auto} · ${pos}</span>
        </label>
      `;
    }).join('');
  }
  // ────────────────────────────────────────────────────────────────────────

  async handleBlogPublish() {
    // Save first if dirty
    if (this.blogDirty || !this.activeBlogPost?.id) {
      await this.handleBlogSave();
    }
    if (!this.activeBlogPost?.id || String(this.activeBlogPost.id).startsWith('new-')) {
      return this.setBlogError("Save the draft before publishing.");
    }

    const platform = this.blogPublishPlatform || this.activeBlogPost?.publish_platform || 'none';
    const postId = this.activeBlogPost.id;
    const btn = this.container.querySelector('[data-testid="blog-footer-publish"]');

    try {
      if (btn) { btn.disabled = true; btn.textContent = 'Publishing...'; }

      if (platform === 'wordpress') {
        // Publish to WordPress
        const res = await fetch(`/api/blog/posts/${postId}/publish-to-wp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-csrf-token': (document.cookie.split('; ').find(c => c.startsWith('csrf_token='))?.split('=')[1] || '') }
        });
        const data = await res.json();
        if (data.ok && data.post) {
          const publishedTitle = data.post.title || this.activeBlogPost?.title || '';
          this.setBlogNotice({
            type: 'published',
            platform: 'wordpress',
            url: data.external_url || '',
            title: publishedTitle
          });
          this.activeBlogPost = data.post;
          this.blogDirty = false;
          this.render();
        } else {
          const msg = (data.error && typeof data.error === 'object' ? data.error.message : data.error) || 'WordPress publish failed.';
          this.setBlogError(msg);
        }
      } else if (platform === 'website') {
        // Publish to Website
        const res = await fetch(`/api/blog/posts/${postId}/publish-to-site`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-csrf-token': (document.cookie.split('; ').find(c => c.startsWith('csrf_token='))?.split('=')[1] || '') }
        });
        const data = await res.json();
        if (data.ok) {
          const publishedTitle = this.activeBlogPost?.title || '';
          this.activeBlogPost.published_to_site = 1;
          this.activeBlogPost.publish_platform = 'website';
          this.activeBlogPost.slug = data.slug || this.activeBlogPost.slug;
          this.blogDirty = false;
          this.setBlogNotice({
            type: 'published',
            platform: 'website',
            url: data.url || '',
            title: publishedTitle
          });
          this.render();
        } else {
          const msg = (data.error && typeof data.error === 'object' ? data.error.message : data.error) || 'Website publish failed.';
          this.setBlogError(msg);
        }
      } else {
        // No platform — just set internal status to published
        const res = await client.saveBlogPost({ ...this.activeBlogPost, status: 'published' });
        if (res?.ok && res.post) {
          const publishedTitle = res.post.title || this.activeBlogPost?.title || '';
          this.activeBlogPost = res.post;
          this.blogDirty = false;
          this.setBlogNotice({
            type: 'published',
            platform: 'internal',
            url: '',
            title: publishedTitle
          });
          this.render();
        } else {
          this.setBlogError(res?.error || "Publish failed.");
        }
      }
    } catch (err) {
      console.error('[BLOG_PUBLISH_ERROR]', err);
      this.setBlogError("Publish failed. Check server connection.");
    }
  }

  async handleBlogConvertSocial(type) {
    if (this.blogDirty) {
      return this.setBlogError("Save your draft before converting to social.");
    }
    if (!this.activeBlogPost?.id || String(this.activeBlogPost.id).startsWith('new-')) {
      return this.setBlogError("Save the draft first.");
    }

    if (type === 'campaign') {
      this.campaignModalOpen = true;
      this.render();
      return;
    }

    // Show platform selection modal
    this._showConvertSocialModal(type);
  }

  _showConvertSocialModal(type) {
    const existing = document.querySelector('.convert-social-overlay');
    if (existing) existing.remove();

    const isSingle = type === 'single';
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay convert-social-overlay';
    overlay.innerHTML = `
      <div class="convert-social-modal">
        <div class="convert-social-head">
          <h2>Convert to ${isSingle ? 'Social Post' : 'Social Posts'}</h2>
          <p>Choose which platforms to generate ${isSingle ? 'a post' : 'posts'} for.</p>
        </div>
        <div class="convert-social-platforms">
          <label class="convert-plat-option"><input type="${isSingle ? 'radio' : 'checkbox'}" name="convert-plat" value="facebook" checked /> <span class="convert-plat-dot" style="background:#1877F2"></span> Facebook</label>
          <label class="convert-plat-option"><input type="${isSingle ? 'radio' : 'checkbox'}" name="convert-plat" value="instagram" ${isSingle ? '' : 'checked'} /> <span class="convert-plat-dot" style="background:#E1306C"></span> Instagram</label>
          <label class="convert-plat-option"><input type="${isSingle ? 'radio' : 'checkbox'}" name="convert-plat" value="x" ${isSingle ? '' : 'checked'} /> <span class="convert-plat-dot" style="background:#71767b"></span> X</label>
          <label class="convert-plat-option"><input type="${isSingle ? 'radio' : 'checkbox'}" name="convert-plat" value="linkedin" ${isSingle ? '' : 'checked'} /> <span class="convert-plat-dot" style="background:#0A66C2"></span> LinkedIn</label>
        </div>
        <div class="convert-social-actions">
          <button class="btn btn-primary convert-social-go">Generate ${isSingle ? 'Post' : 'Posts'}</button>
          <button class="btn btn-glass convert-social-cancel">Cancel</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    overlay.querySelector('.convert-social-cancel').onclick = () => overlay.remove();
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

    overlay.querySelector('.convert-social-go').onclick = async () => {
      const checked = overlay.querySelectorAll('input[name="convert-plat"]:checked');
      const platforms = Array.from(checked).map(c => c.value);
      if (platforms.length === 0) return;

      const btn = overlay.querySelector('.convert-social-go');
      btn.disabled = true;
      btn.textContent = 'Generating...';

      try {
        const res = await client.generateSocialFromBlog({
          blog_post_id: this.activeBlogPost.id,
          platforms: isSingle ? [platforms[0]] : platforms
        });
        overlay.remove();
        if (res?.ok) {
          this.setBlogNotice(`${isSingle ? 'Post' : 'Posts'} created. Switching to Social view...`);
          this.activeCreateMode = 'social';
          localStorage.setItem('activeCreateMode', 'social');
          this.items = res.items || [];
          this.render();
        }
      } catch (err) {
        overlay.remove();
        this.setBlogError(err.message || 'Failed to generate social content');
      }
    };
  }

  async _legacyBlogConvertSocial(type) {
    const activePlatforms = Object.keys(this.platforms).filter(p => this.platforms[p]);
    try {
      const res = await client.generateSocialFromBlog({
        blog_post_id: this.activeBlogPost.id,
        platforms: type === 'single' ? [activePlatforms[0]] : activePlatforms
      });

      if (res?.ok) {
        this.setBlogNotice("Social " + (type === 'single' ? 'post' : 'posts') + " created. Switching to Social view...");
        this.activeCreateMode = 'social';
        localStorage.setItem('activeCreateMode', 'social');
        this.items = res.items || [];
        if (this.items.length > 0) {
          this.selectedItemId = this.items[0].id;
          this.activeDraft = null;
        }
        this.render();
      } else {
        this.setBlogError(res?.error?.message || res?.error || "Social generation failed.");
      }
    } catch (err) {
      console.error('[BLOG_CONVERT_SOCIAL_ERROR]', err);
      this.setBlogError("Social conversion failed.");
    }
  }

  async handleRegenerate(itemId) {
    const item = this.items.find(i => i.id === itemId);
    if (!item) return;

    this.loading = true; // Global spinner for simplicity or specific one
    this.render();

    try {
      const regenTemplatePrompt = this.socialTemplate !== 'freeform' ? (SOCIAL_TEMPLATES.find(t => t.key === this.socialTemplate)?.prompt || '') : '';
      const res = await client.generateBatch({
        topic: this.topic,
        platforms: [{ platform: item.platform, sessionContext: item.context || '' }],
        toneOverride: this.getEffectiveTone(),
        postLength: this.postLength,
        sessionContext: regenTemplatePrompt ? `[CONTENT FRAMEWORK] ${regenTemplatePrompt}` : undefined
      });

      const drafts = Array.isArray(res.data) ? res.data : (res.data?.drafts || res.drafts || []);
      if (drafts.length > 0) {
        item.text = drafts[0].text;
        // Merge metadata if present
        item.meta = { ...(item.meta || {}), ...(drafts[0].meta || {}), hashtags: drafts[0].hashtags || [] };
        this.socialDirty = true;

        // Direct DOM update for reliability in tests
        const editor = this.container.querySelector('#editor-content');
        if (editor && (this.activeDraft?.id === item.id || this.selectedItemId === item.id)) {
          editor.value = item.text;
        }
        const preview = this.container.querySelector(`.create-item-card[data-item-id="${item.id}"] .create-item-preview`);
        if (preview) {
          preview.textContent = this.truncate(item.text, 60);
        }
      }
    } catch (err) {
      console.error('[REGEN_ERROR]', err);
      this.socialToast = { msg: 'Regeneration failed', kind: 'error' };
    } finally {
      this.loading = false;
      this.render();
    }
  }

  async unmount() {
    if (this._beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler);
      this._beforeUnloadHandler = null;
    }
    if (this.splitTimeout) { clearTimeout(this.splitTimeout); this.splitTimeout = null; }
    if (this._toastTimer) { clearTimeout(this._toastTimer); this._toastTimer = null; }
    if (this.blogBannerTimeout) { clearTimeout(this.blogBannerTimeout); this.blogBannerTimeout = null; }
  }
}

